import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './App.css';
import DragonRaceArena from './DragonRaceArena';
import useWebSocket from './useWebSocket';
import { userDocumentation } from './userDocumentation';

const MAX_STORE_HEADER_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;
const STORE_HEADER_IMAGE_MAX_WIDTH = 1800;
const STORE_HEADER_IMAGE_MAX_HEIGHT = 720;
const STORE_HEADER_IMAGE_OUTPUT_QUALITY = 0.82;
const DRAGON_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'];
const LANGUAGE_STORAGE_KEY = 'edh_language';
const RACE_CATCHUP_MODE_STORAGE_KEY = 'edh_race_catchup_mode';
const RACE_CATCHUP_INTENSITY_STORAGE_KEY = 'edh_race_catchup_intensity';
const RACE_TV_ULTRA_FLUID_STORAGE_KEY = 'edh_race_tv_ultra_fluid';
const ROUND_TIMER_DEFAULT_MINUTES = 75;

const TRANSLATIONS = {
  fr: {
    helpAriaLabel: 'Ouvrir la documentation utilisateur',
    helpTitle: 'Documentation utilisateur',
    loginTitle: 'Connexion',
    loginSubtitle: 'Entre ton code utilisateur pour accéder à tes magasins.',
    userCodePlaceholder: 'Code utilisateur',
    passwordPlaceholder: 'Mot de passe (laisser vide pour un code invité)',
    loginDenied: 'Connexion refusée.',
    loginLoading: 'Connexion...',
    loginButton: 'Se connecter',
    appSubtitle: 'Application de gestion de tournois Magic: The Gathering Commander',
    activeStoreBadge: 'Magasin actif',
    connectedAs: 'Connecte en tant que {label} ({role})',
    superuser: 'superuser',
    user: 'utilisateur',
    logout: 'Deconnexion',
    storesTitle: 'Magasins',
    storesSubtitle: 'Chaque magasin a ses propres joueurs et tournois.',
    createStore: '+ Creer un magasin',
    activeStoreLabel: 'Magasin actif',
    selectStorePlaceholder: '-- Selectionner un magasin --',
    edit: 'Editer',
    delete: 'Supprimer',
    playersRequiredName: 'Le nom du joueur est requis',
    playersRequiredEmail: 'L\'email du joueur est requis',
    invalidEmail: 'Email invalide',
    tournamentRequiredName: 'Le nom du tournoi est requis',
    tournamentRequiredPlayer: 'Au moins un joueur est requis',
    playerUpdated: 'Joueur modifie avec succes',
    playerCreated: 'Joueur ajoute avec succes',
    playerDeleted: 'Joueur supprime avec succes',
    saveError: 'Erreur lors de la sauvegarde',
    deleteError: 'Erreur lors de la suppression',
    deletePlayerConfirm: 'Etes-vous sur de vouloir supprimer ce joueur ?',
    importNoValidPlayer: 'Aucun joueur valide trouve dans le fichier',
    importPlayerSuccess: '{count} joueur(s) importe(s) avec succes',
    importError: 'Erreur lors de l\'import des joueurs',
    tournamentCreated: 'Tournoi cree avec succes',
    tournamentCreateError: 'Erreur lors de la creation du tournoi',
    lineInvalidFormat: 'Ligne {line}: Format invalide (attendu: nom, email)',
    lineMissingNameOrEmail: 'Ligne {line}: Nom ou email vide',
    lineInvalidEmail: 'Ligne {line}: Email invalide ({email})',
  },
  en: {
    helpAriaLabel: 'Open user documentation',
    helpTitle: 'User documentation',
    loginTitle: 'Sign in',
    loginSubtitle: 'Enter your user code to access your stores.',
    userCodePlaceholder: 'User code',
    passwordPlaceholder: 'Password (leave blank for guest code)',
    loginDenied: 'Login denied.',
    loginLoading: 'Signing in...',
    loginButton: 'Sign in',
    appSubtitle: 'Tournament manager for Magic: The Gathering Commander',
    activeStoreBadge: 'Active store',
    connectedAs: 'Signed in as {label} ({role})',
    superuser: 'superuser',
    user: 'user',
    logout: 'Sign out',
    storesTitle: 'Stores',
    storesSubtitle: 'Each store has its own players and tournaments.',
    createStore: '+ Create store',
    activeStoreLabel: 'Active store',
    selectStorePlaceholder: '-- Select a store --',
    edit: 'Edit',
    delete: 'Delete',
    playersRequiredName: 'Player name is required',
    playersRequiredEmail: 'Player email is required',
    invalidEmail: 'Invalid email',
    tournamentRequiredName: 'Tournament name is required',
    tournamentRequiredPlayer: 'At least one player is required',
    playerUpdated: 'Player updated successfully',
    playerCreated: 'Player added successfully',
    playerDeleted: 'Player deleted successfully',
    saveError: 'Error while saving',
    deleteError: 'Error while deleting',
    deletePlayerConfirm: 'Are you sure you want to delete this player?',
    importNoValidPlayer: 'No valid players found in the file',
    importPlayerSuccess: '{count} player(s) imported successfully',
    importError: 'Error while importing players',
    tournamentCreated: 'Tournament created successfully',
    tournamentCreateError: 'Error while creating tournament',
    lineInvalidFormat: 'Line {line}: Invalid format (expected: name, email)',
    lineMissingNameOrEmail: 'Line {line}: Name or email is empty',
    lineInvalidEmail: 'Line {line}: Invalid email ({email})',
  },
};

const interpolateText = (template, params = {}) =>
  String(template).replace(/\{(\w+)\}/g, (_, key) => String(params[key] ?? `{${key}}`));

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Impossible de lire ce fichier.'));
    reader.readAsDataURL(file);
  });

const loadImageElement = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Impossible de préparer cette image.'));
    image.src = src;
  });

const resizeImageDataUrl = async (dataUrl) => {
  const image = await loadImageElement(dataUrl);
  const widthRatio = STORE_HEADER_IMAGE_MAX_WIDTH / image.width;
  const heightRatio = STORE_HEADER_IMAGE_MAX_HEIGHT / image.height;
  const ratio = Math.min(widthRatio, heightRatio, 1);
  const targetWidth = Math.max(1, Math.round(image.width * ratio));
  const targetHeight = Math.max(1, Math.round(image.height * ratio));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Impossible de préparer l\'image dans ce navigateur.');
  }

  context.drawImage(image, 0, 0, targetWidth, targetHeight);
  return canvas.toDataURL('image/jpeg', STORE_HEADER_IMAGE_OUTPUT_QUALITY);
};

function App() {
  const [roundTabParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const storeId = Number(params.get('storeId'));
    const tournamentId = Number(params.get('tournamentId'));
    const roundId = Number(params.get('roundId'));
    const fullscreen = params.get('fullscreen') === '1';

    if (
      Number.isInteger(storeId) &&
      storeId > 0 &&
      Number.isInteger(tournamentId) &&
      tournamentId > 0 &&
      Number.isInteger(roundId) &&
      roundId > 0
    ) {
      return { storeId, tournamentId, roundId, fullscreen };
    }

    return null;
  });

  const [raceTabParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const race = params.get('race') === '1';
    const tv = params.get('tv') === '1';
    const ultra = params.get('ultra') === '1';
    const storeId = Number(params.get('storeId'));
    const tournamentId = Number(params.get('tournamentId'));

    if (race && Number.isInteger(storeId) && storeId > 0 && Number.isInteger(tournamentId) && tournamentId > 0) {
      return { storeId, tournamentId, tv, ultra };
    }

    return null;
  });

  const [stores, setStores] = useState([]);
  const [authSession, setAuthSession] = useState(null);
  const [authCodeInput, setAuthCodeInput] = useState('');
  const [authPasswordInput, setAuthPasswordInput] = useState('');
  const [showAuthPassword, setShowAuthPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showAccessCodeForm, setShowAccessCodeForm] = useState(false);
  const [showAccessCodePassword, setShowAccessCodePassword] = useState(false);
  const [editingAccessCodeId, setEditingAccessCodeId] = useState(null);
  const [accessCodes, setAccessCodes] = useState([]);
  const [accessCodesCollapsed, setAccessCodesCollapsed] = useState(false);
  const [newAccessCode, setNewAccessCode] = useState({
    code: '',
    label: '',
    password: '',
    role: 'user',
    stores: [],
  });
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [showStoreForm, setShowStoreForm] = useState(false);
  const [showEditStoreForm, setShowEditStoreForm] = useState(false);
  const [newStoreName, setNewStoreName] = useState('');
  const [newStoreImageUrl, setNewStoreImageUrl] = useState('');
  const [isCreateStoreImageDragging, setIsCreateStoreImageDragging] = useState(false);
  const [editStoreName, setEditStoreName] = useState('');
  const [editStoreImageUrl, setEditStoreImageUrl] = useState('');
  const [isEditStoreImageDragging, setIsEditStoreImageDragging] = useState(false);
  const [players, setPlayers] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [tournamentPlayers, setTournamentPlayers] = useState([]);
  const [tournamentRounds, setTournamentRounds] = useState([]);
  const [roundResults, setRoundResults] = useState([]);
  const [showPlayerForm, setShowPlayerForm] = useState(false);
  const [showTournamentForm, setShowTournamentForm] = useState(false);
  const [showEditTournamentForm, setShowEditTournamentForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editingTable, setEditingTable] = useState(null);
  const [editingRoundId, setEditingRoundId] = useState(null);
  const [editingTableDraft, setEditingTableDraft] = useState({});
  const [selectedRoundView, setSelectedRoundView] = useState(null);
  const [absentNextRoundIds, setAbsentNextRoundIds] = useState([]);
  const [droppedPlayerIds, setDroppedPlayerIds] = useState([]);
  const [newPlayer, setNewPlayer] = useState({ name: '', email: '', is_regular: false });
  const [newTournament, setNewTournament] = useState({ name: '', selectedPlayers: [] });
  const [editTournament, setEditTournament] = useState({ name: '', status: 'pending', selectedPlayers: [] });
  const [cardSearchQuery, setCardSearchQuery] = useState('');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [playersCollapsed, setPlayersCollapsed] = useState(false);
  const [tournamentsCollapsed, setTournamentsCollapsed] = useState(false);
  const [roundDurationMinutes, setRoundDurationMinutes] = useState(ROUND_TIMER_DEFAULT_MINUTES);
  const [, setRoundTimerStorageVersion] = useState(0);
  const [raceDurationMinutes, setRaceDurationMinutes] = useState(10);
  const [raceCatchupMode, setRaceCatchupMode] = useState(() => {
    const storedMode = localStorage.getItem(RACE_CATCHUP_MODE_STORAGE_KEY);
    return storedMode === 'arcade' ? 'arcade' : 'balanced';
  });
  const [raceCatchupIntensity, setRaceCatchupIntensity] = useState(() => {
    const storedIntensity = localStorage.getItem(RACE_CATCHUP_INTENSITY_STORAGE_KEY);
    return ['low', 'normal', 'high'].includes(storedIntensity) ? storedIntensity : 'normal';
  });
  const [raceTvUltraFluid, setRaceTvUltraFluid] = useState(() => {
    if (raceTabParams?.tv) {
      return Boolean(raceTabParams.ultra);
    }
    return localStorage.getItem(RACE_TV_ULTRA_FLUID_STORAGE_KEY) === '1';
  });
  const [raceIsRunning, setRaceIsRunning] = useState(false);
  const [raceStartedAt, setRaceStartedAt] = useState(null);
  const [raceElapsedMs, setRaceElapsedMs] = useState(0);
  const [raceParticipants, setRaceParticipants] = useState([]);
  const [raceCountdown, setRaceCountdown] = useState(null);
  const [raceGoFlash, setRaceGoFlash] = useState(false);
  const [raceWinner, setRaceWinner] = useState(null);
  const raceWinnerRef = useRef(null);
  const raceBroadcastChannelRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [playerSearchFilter, setPlayerSearchFilter] = useState('');
  const [showRegularPlayersOnly, setShowRegularPlayersOnly] = useState(() => {
    try {
      return sessionStorage.getItem('edh_players_regular_only') === '1';
    } catch {
      return false;
    }
  });
  const [showImportPlayersModal, setShowImportPlayersModal] = useState(false);
  const [importingPlayers, setImportingPlayers] = useState(false);
  const [cumulativeStandings, setCumulativeStandings] = useState([]);
  const [cumulativeStartDate, setCumulativeStartDate] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-01-01`;
  });
  const [cumulativeEndDate, setCumulativeEndDate] = useState('');
  const cumulativeStartDateRef = useRef(cumulativeStartDate);
  const cumulativeEndDateRef = useRef(cumulativeEndDate);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight || 900);
  const [language, setLanguage] = useState(() => {
    const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return storedLanguage === 'en' ? 'en' : 'fr';
  });

  const locale = language === 'en' ? 'en-US' : 'fr-FR';
  const t = useCallback((key, params = {}) => {
    const activeDictionary = TRANSLATIONS[language] || TRANSLATIONS.fr;
    const fallback = TRANSLATIONS.fr[key] || key;
    const resolved = activeDictionary[key] || fallback;
    return interpolateText(resolved, params);
  }, [language]);
  const tr = useCallback((frText, enText) => (language === 'en' ? enText : frText), [language]);

  useEffect(() => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }, [language]);

  useEffect(() => {
    localStorage.setItem(RACE_CATCHUP_MODE_STORAGE_KEY, raceCatchupMode);
  }, [raceCatchupMode]);

  useEffect(() => {
    localStorage.setItem(RACE_CATCHUP_INTENSITY_STORAGE_KEY, raceCatchupIntensity);
  }, [raceCatchupIntensity]);

  useEffect(() => {
    if (raceTabParams?.tv) return;
    localStorage.setItem(RACE_TV_ULTRA_FLUID_STORAGE_KEY, raceTvUltraFluid ? '1' : '0');
  }, [raceTvUltraFluid, raceTabParams]);

  useEffect(() => {
    if (!selectedTournament?.id) {
      setRoundDurationMinutes(ROUND_TIMER_DEFAULT_MINUTES);
      return;
    }

    const storedDuration = Number(localStorage.getItem(getRoundTimerDurationStorageKey(selectedTournament.id)));
    if (Number.isInteger(storedDuration) && storedDuration > 0) {
      setRoundDurationMinutes(Math.max(1, Math.min(300, storedDuration)));
      return;
    }

    setRoundDurationMinutes(ROUND_TIMER_DEFAULT_MINUTES);
  }, [selectedTournament?.id]);

  useEffect(() => {
    if (!selectedTournament?.id) return;
    localStorage.setItem(
      getRoundTimerDurationStorageKey(selectedTournament.id),
      String(Math.max(1, Math.min(300, Number(roundDurationMinutes) || ROUND_TIMER_DEFAULT_MINUTES)))
    );
  }, [selectedTournament?.id, roundDurationMinutes]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (!event.key || !event.key.startsWith('edh_round_timer_')) return;
      setRoundTimerStorageVersion((value) => value + 1);
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    cumulativeStartDateRef.current = cumulativeStartDate;
    cumulativeEndDateRef.current = cumulativeEndDate;
  }, [cumulativeStartDate, cumulativeEndDate]);

  // Handle WebSocket updates
  const handleWebSocketUpdate = useCallback((updateType, updateData) => {
    if (!selectedStoreId) return;
    
    // Refresh data based on update type
    if (updateType === 'players') {
      fetchPlayers(selectedStoreId);
    } else if (updateType === 'tournaments') {
      fetchTournaments(selectedStoreId);
    } else if (updateType === 'tournamentPlayers') {
      if (selectedTournament?.id) {
        fetchTournamentPlayers(selectedTournament.id);
      }
    } else if (updateType === 'rounds') {
      if (selectedTournament?.id) {
        fetchTournamentRounds(selectedTournament.id);
      }
    }
  }, [selectedStoreId, selectedTournament?.id]);

  const { emit: socketEmit } = useWebSocket(handleWebSocketUpdate);

  // Join store room when store is selected
  useEffect(() => {
    if (selectedStoreId && socketEmit) {
      socketEmit('join-store', selectedStoreId);
    }
  }, [selectedStoreId, socketEmit]);

  const showNotification = (message, type = 'info', duration = 4000) => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, type }]);
    if (duration > 0) {
      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }, duration);
    }
    return id;
  };

  const validatePlayerForm = () => {
    if (!newPlayer.name || !newPlayer.name.trim()) {
      showNotification(t('playersRequiredName'), 'error');
      return false;
    }
    if (!newPlayer.email || !newPlayer.email.trim()) {
      showNotification(t('playersRequiredEmail'), 'error');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newPlayer.email)) {
      showNotification(t('invalidEmail'), 'error');
      return false;
    }
    return true;
  };

  const validateTournamentForm = () => {
    if (!newTournament.name || !newTournament.name.trim()) {
      showNotification(t('tournamentRequiredName'), 'error');
      return false;
    }
    if (newTournament.selectedPlayers.length === 0) {
      showNotification(t('tournamentRequiredPlayer'), 'error');
      return false;
    }
    return true;
  };

  const sortPlayersByName = (list) =>
    [...list].sort((a, b) => {
      const regularDelta = Number(Boolean(b.is_regular)) - Number(Boolean(a.is_regular));
      if (regularDelta !== 0) return regularDelta;
      return a.name.localeCompare(b.name, language, { sensitivity: 'base' });
    });

  const filteredPlayers = sortPlayersByName(players).filter((p) =>
    (p.name.toLowerCase().includes(playerSearchFilter.toLowerCase()) ||
    p.email.toLowerCase().includes(playerSearchFilter.toLowerCase())) &&
    (!showRegularPlayersOnly || Boolean(p.is_regular))
  );
  const sortedPlayers = filteredPlayers;
  const globalCumulativeStandings = cumulativeStandings
    .filter((player) => Number(player.career_total_points || 0) > 0)
    .sort((a, b) => {
    if (Number(b.career_total_points || 0) !== Number(a.career_total_points || 0)) return Number(b.career_total_points || 0) - Number(a.career_total_points || 0);
    if (Number(b.career_wins || 0) !== Number(a.career_wins || 0)) return Number(b.career_wins || 0) - Number(a.career_wins || 0);
    if (Number(b.career_second_places || 0) !== Number(a.career_second_places || 0)) return Number(b.career_second_places || 0) - Number(a.career_second_places || 0);
    if (Number(b.career_third_places || 0) !== Number(a.career_third_places || 0)) return Number(b.career_third_places || 0) - Number(a.career_third_places || 0);
    if (Number(b.career_fourth_places || 0) !== Number(a.career_fourth_places || 0)) return Number(b.career_fourth_places || 0) - Number(a.career_fourth_places || 0);
    if (Number(b.career_draws || 0) !== Number(a.career_draws || 0)) return Number(b.career_draws || 0) - Number(a.career_draws || 0);
    return a.name.localeCompare(b.name, language, { sensitivity: 'base' });
  });
  const standingsRankByPlayerId = new Map(globalCumulativeStandings.map((player, index) => [player.id, index + 1]));
  const getPlayerBadges = (player) => {
    const wins = Number(player.career_wins || 0);
    const second = Number(player.career_second_places || 0);
    const third = Number(player.career_third_places || 0);
    const fourth = Number(player.career_fourth_places || 0);
    const draws = Number(player.career_draws || 0);
    const podiumFinishes = second + third + fourth;
    const rank = standingsRankByPlayerId.get(player.id) || null;
    const badges = [];

    if (rank === 1 && wins > 0) {
      badges.push({
        key: 'top_local',
        label: tr('Top 1 local', 'Local #1'),
        className: 'bg-amber-900/70 text-amber-100 border border-amber-600/60',
      });
    }

    if (wins >= 10) {
      badges.push({
        key: 'win_machine',
        label: tr('Machine a victoires', 'Win machine'),
        className: 'bg-yellow-900/70 text-yellow-100 border border-yellow-600/60',
      });
    }

    if (podiumFinishes >= 12) {
      badges.push({
        key: 'podium_star',
        label: tr('Specialiste podium', 'Podium specialist'),
        className: 'bg-indigo-900/70 text-indigo-100 border border-indigo-600/60',
      });
    }

    if (draws >= 5) {
      badges.push({
        key: 'draw_master',
        label: tr('Maitre des draws', 'Draw master'),
        className: 'bg-sky-900/70 text-sky-100 border border-sky-600/60',
      });
    }

    if (player.is_regular && wins + podiumFinishes + draws >= 8) {
      badges.push({
        key: 'club_veteran',
        label: tr('Veteran du club', 'Club veteran'),
        className: 'bg-cyan-900/70 text-cyan-100 border border-cyan-600/60',
      });
    }

    return badges.slice(0, 3);
  };
  const sortedTournamentPlayers = sortPlayersByName(tournamentPlayers);
  const selectedStore = stores.find((store) => store.id === selectedStoreId) || null;
  const isSuperuser = authSession?.role === 'superuser';
  const hasStoreBanner = Boolean(selectedStore?.header_image_url);

  const getStoreAccessLevel = (storeId) => {
    if (!authSession) return null;
    if (authSession.role === 'superuser') return 'edit';
    const permission = (authSession.permissions || []).find((p) => p.store_id === Number(storeId));
    return permission?.access_level || null;
  };

  const canEditCurrentStore = Boolean(selectedStoreId && getStoreAccessLevel(selectedStoreId) === 'edit');

  const applyAccessCredentials = (code, password) => {
    if (code) {
      axios.defaults.headers.common['x-access-code'] = code;
    } else {
      delete axios.defaults.headers.common['x-access-code'];
    }

    if (password) {
      axios.defaults.headers.common['x-access-password'] = password;
    } else {
      delete axios.defaults.headers.common['x-access-password'];
    }
  };

  const getPresenceStorageKey = (tournamentId) => `edh_presence_${tournamentId}`;

  const getDraftStorageKey = (tournamentId, roundId, tableNumber) =>
    `edh_result_draft_${tournamentId}_${roundId}_${tableNumber}`;

  const getRoundTimerDurationStorageKey = (tournamentId) => `edh_round_timer_duration_${tournamentId}`;
  const getRoundTimerStorageKey = (roundId) => `edh_round_timer_${roundId}`;

  const persistPresenceState = (tournamentId, absentIds, droppedIds) => {
    if (!tournamentId) return;
    localStorage.setItem(
      getPresenceStorageKey(tournamentId),
      JSON.stringify({ absentNextRoundIds: absentIds, droppedPlayerIds: droppedIds })
    );
  };

  const readRoundTimerFallback = (roundId) => {
    if (!roundId) return null;
    try {
      const raw = localStorage.getItem(getRoundTimerStorageKey(roundId));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return {
        durationMinutes: clampRoundTimerMinutes(parsed?.durationMinutes),
        startedAt: parsed?.startedAt || null,
        elapsedSeconds: Math.max(0, Number(parsed?.elapsedSeconds) || 0),
      };
    } catch {
      return null;
    }
  };

  const writeRoundTimerFallback = (roundId, nextState) => {
    if (!roundId) return;
    localStorage.setItem(getRoundTimerStorageKey(roundId), JSON.stringify(nextState));
    setRoundTimerStorageVersion((value) => value + 1);
  };

  const formatTiebreakValue = (value) => Number(value || 0).toFixed(2);
  const clampRoundTimerMinutes = (value) => Math.max(1, Math.min(300, Number(value) || ROUND_TIMER_DEFAULT_MINUTES));
  const raceDurationMs = Math.max(1, Number(raceDurationMinutes) || 1) * 60 * 1000;
  const raceTickMs = raceTvUltraFluid ? 16 : 33;

  const formatRoundTimerClock = (totalMs) => {
    const totalSeconds = Math.max(0, Math.ceil(totalMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const getRoundTimerState = (round) => {
    const hasBackendTimer = Boolean(
      round && (
        Object.prototype.hasOwnProperty.call(round, 'timer_duration_minutes')
        || Object.prototype.hasOwnProperty.call(round, 'timer_started_at')
        || Object.prototype.hasOwnProperty.call(round, 'timer_elapsed_seconds')
      )
    );
    const fallbackTimer = hasBackendTimer ? null : readRoundTimerFallback(round?.id);
    const durationMinutes = clampRoundTimerMinutes(hasBackendTimer ? round?.timer_duration_minutes : fallbackTimer?.durationMinutes);
    const durationMs = durationMinutes * 60 * 1000;
    const persistedElapsedSeconds = Math.max(0, Number(hasBackendTimer ? round?.timer_elapsed_seconds : fallbackTimer?.elapsedSeconds) || 0);
    const startedAt = hasBackendTimer ? round?.timer_started_at : fallbackTimer?.startedAt;
    const liveElapsedSeconds = startedAt
      ? Math.max(0, Math.floor((currentTime.getTime() - new Date(startedAt).getTime()) / 1000))
      : 0;
    const elapsedMs = (persistedElapsedSeconds + liveElapsedSeconds) * 1000;
    const remainingMs = Math.max(0, durationMs - elapsedMs);

    return {
      durationMinutes,
      durationMs,
      elapsedMs,
      remainingMs,
      isRunning: Boolean(startedAt),
      isExpired: remainingMs === 0,
      usesFallback: !hasBackendTimer,
    };
  };

  const formatRaceClock = (totalMs) => {
    const clamped = Math.max(0, Math.floor(totalMs / 1000));
    const minutes = String(Math.floor(clamped / 60)).padStart(2, '0');
    const seconds = String(clamped % 60).padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const getDragonBoostProfile = (dragonId) => {
    const numericId = Number(dragonId) || 0;
    return {
      cycleMs: 2400 + (numericId % 5) * 210,
      boostWindowMs: 520 + (numericId % 3) * 70,
      minBonusMultiplier: 0.28,
      maxBonusMultiplier: 0.9,
      phaseOffsetMs: numericId * 173,
    };
  };

  const getTrianglePulseArea = (normalizedProgress) => {
    const clamped = Math.max(0, Math.min(1, normalizedProgress));
    if (clamped <= 0.5) {
      return clamped * clamped;
    }

    return (2 * clamped) - (clamped * clamped) - 0.5;
  };

  const getBoostExtraWithinCycle = (elapsedInCycleMs, boostWindowMs, minBonusMultiplier, maxBonusMultiplier) => {
    const activeMs = Math.max(0, Math.min(boostWindowMs, elapsedInCycleMs));
    if (activeMs <= 0) return 0;

    const normalizedActive = activeMs / boostWindowMs;
    const variableBonusMultiplier = maxBonusMultiplier - minBonusMultiplier;

    return (
      (activeMs * minBonusMultiplier)
      + (boostWindowMs * getTrianglePulseArea(normalizedActive) * variableBonusMultiplier)
    );
  };

  const getDragonBoostExtraElapsedMs = (dragonId, elapsedMs) => {
    const {
      cycleMs,
      boostWindowMs,
      minBonusMultiplier,
      maxBonusMultiplier,
      phaseOffsetMs,
    } = getDragonBoostProfile(dragonId);

    const getPeriodicExtra = (totalMs) => {
      const safeMs = Math.max(0, totalMs);
      const fullCycles = Math.floor(safeMs / cycleMs);
      const elapsedInCycleMs = safeMs % cycleMs;
      const extraPerCycle = getBoostExtraWithinCycle(
        boostWindowMs,
        boostWindowMs,
        minBonusMultiplier,
        maxBonusMultiplier
      );

      return (
        (fullCycles * extraPerCycle)
        + getBoostExtraWithinCycle(
          elapsedInCycleMs,
          boostWindowMs,
          minBonusMultiplier,
          maxBonusMultiplier
        )
      );
    };

    return getPeriodicExtra(phaseOffsetMs + elapsedMs) - getPeriodicExtra(phaseOffsetMs);
  };

  const getDragonBoost = (dragonId, elapsedMs) => {
    const {
      cycleMs,
      boostWindowMs,
      minBonusMultiplier,
      maxBonusMultiplier,
      phaseOffsetMs,
    } = getDragonBoostProfile(dragonId);
    const phase = (elapsedMs + phaseOffsetMs) % cycleMs;
    const extraElapsedMs = getDragonBoostExtraElapsedMs(dragonId, elapsedMs);

    if (phase >= boostWindowMs) {
      return { isActive: false, multiplier: 1, extraElapsedMs };
    }

    const normalized = phase / boostWindowMs;
    const pulse = 1 - Math.abs(normalized * 2 - 1);
    const bonusMultiplier = minBonusMultiplier + (pulse * (maxBonusMultiplier - minBonusMultiplier));
    return {
      isActive: true,
      multiplier: 1 + bonusMultiplier,
      extraElapsedMs,
    };
  };

  const getAdaptiveCatchupIncrementMs = (gapRatio, playerProgressPercent, tickMs = 33) => {
    const intensityMultiplier = raceCatchupIntensity === 'low'
      ? 0.82
      : raceCatchupIntensity === 'high'
      ? 1.38
      : 1;

    const modeConfig = raceCatchupMode === 'arcade'
      ? {
          minGapToActivate: 0.045,
          gapWindow: 0.24,
          basePower: 0.3,
          linearPower: 1.18,
          quadraticPower: 1.95,
          tierMedium: 0.48,
          tierHigh: 0.95,
          tierMax: 1.42,
          endDampingMin: 0.2,
          earlyBoost: 1.25,
        }
      : {
          minGapToActivate: 0.065,
          gapWindow: 0.3,
          basePower: 0.16,
          linearPower: 0.7,
          quadraticPower: 1.05,
          tierMedium: 0.32,
          tierHigh: 0.62,
          tierMax: 0.94,
          endDampingMin: 0.35,
          earlyBoost: 1.08,
        };

    if (gapRatio <= modeConfig.minGapToActivate) return 0;

    const normalizedGap = Math.min(1, Math.max(0, (gapRatio - modeConfig.minGapToActivate) / modeConfig.gapWindow));
    const nonlinearPower = modeConfig.basePower
      + (modeConfig.linearPower * normalizedGap)
      + (modeConfig.quadraticPower * normalizedGap * normalizedGap);

    let tierBonus = 0;
    if (gapRatio > 0.3) tierBonus = modeConfig.tierMax;
    else if (gapRatio > 0.22) tierBonus = modeConfig.tierHigh;
    else if (gapRatio > 0.14) tierBonus = modeConfig.tierMedium;

    const clampedProgress = Math.max(0, Math.min(100, playerProgressPercent));
    const earlyRaceBoost = clampedProgress < 70 ? modeConfig.earlyBoost : 1;

    // Reduce the rubber-band near finish so it does not feel unfair in the last stretch.
    const endRaceDamping = clampedProgress >= 85
      ? Math.max(modeConfig.endDampingMin, 1 - ((clampedProgress - 85) / 15))
      : 1;

    return tickMs * (nonlinearPower + tierBonus) * intensityMultiplier * earlyRaceBoost * endRaceDamping;
  };

  const getDragonProgressPercent = (dragon, elapsedMs) => {
    const boost = getDragonBoost(dragon.id, elapsedMs);
    const effectiveElapsed = elapsedMs + boost.extraElapsedMs + (dragon.catchupBonusMs || 0);
    return Math.min(100, (effectiveElapsed / dragon.finishTimeMs) * 100);
  };

  const buildRoundTabUrl = (storeId, tournamentId, roundId, fullscreen = false) => {
    const fullscreenParam = fullscreen ? '&fullscreen=1' : '';
    return `${window.location.origin}/?storeId=${storeId}&tournamentId=${tournamentId}&roundId=${roundId}${fullscreenParam}`;
  };

  const buildRaceTabUrl = (storeId, tournamentId, tvMode = false, ultraFluid = false) => {
    const tvParam = tvMode ? '&tv=1' : '';
    const ultraParam = ultraFluid ? '&ultra=1' : '';
    return `${window.location.origin}/?race=1&storeId=${storeId}&tournamentId=${tournamentId}${tvParam}${ultraParam}`;
  };

  const openCardSearch = () => {
    const term = cardSearchQuery.trim();
    const baseUrl = 'https://facetofacegames.com/search?type=product&q=';
    const searchUrl = `${baseUrl}${encodeURIComponent(term)}`;
    window.open(searchUrl, '_blank', 'noopener,noreferrer');
  };

  const importStoreHeaderImage = async (file, setter) => {
    if (!file) {
      setter('');
      return;
    }

    if (!String(file.type || '').startsWith('image/')) {
      alert(tr('Le fichier selectionne doit etre une image.', 'The selected file must be an image.'));
      return;
    }

    try {
      const sourceDataUrl = await readFileAsDataUrl(file);
      const optimizedDataUrl = await resizeImageDataUrl(sourceDataUrl);

      if (optimizedDataUrl.length > MAX_STORE_HEADER_IMAGE_SIZE_BYTES * 1.37) {
        alert(tr('Cette image reste trop lourde apres optimisation. Essaie une image plus legere.', 'This image is still too large after optimization. Try a lighter image.'));
        return;
      }

      setter(optimizedDataUrl);
    } catch (error) {
      alert(error.message || tr('Impossible d\'importer cette image.', 'Unable to import this image.'));
    }
  };

  const handleStoreImageDrop = async (event, setter, draggingSetter) => {
    event.preventDefault();
    draggingSetter(false);
    await importStoreHeaderImage(event.dataTransfer?.files?.[0], setter);
  };

  // Fonctions API de base
  const fetchStores = async () => {
    try {
      const response = await axios.get('/api/stores');
      setStores(response.data || []);
      return response.data || [];
    } catch (error) {
      console.error('Erreur lors du chargement des magasins:', error);
      return [];
    }
  };

  const fetchAccessCodes = useCallback(async () => {
    if (!isSuperuser) return;
    try {
      const response = await axios.get('/api/access-codes');
      setAccessCodes(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des codes utilisateurs:', error);
    }
  }, [isSuperuser]);

  const fetchPlayers = async (storeId) => {
    if (!storeId) {
      setPlayers([]);
      return;
    }

    try {
      const response = await axios.get('/api/players', {
        params: { store_id: storeId },
      });
      setPlayers(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des joueurs:', error);
    }
  };

  const fetchCumulativeStandings = useCallback(async (storeId, startDate, endDate) => {
    if (!storeId) {
      setCumulativeStandings([]);
      return;
    }

    if (!startDate) {
      setCumulativeStandings([]);
      return;
    }

    try {
      const params = {
        store_id: storeId,
        start_date: startDate,
      };
      if (endDate) {
        params.end_date = endDate;
      }

      const response = await axios.get('/api/players', { params });
      setCumulativeStandings(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement du classement cumulatif:', error);
      setCumulativeStandings([]);
    }
  }, []);

  const fetchTournaments = async (storeId) => {
    if (!storeId) {
      setTournaments([]);
      return [];
    }

    try {
      const response = await axios.get('/api/tournaments', {
        params: { store_id: storeId },
      });
      setTournaments(response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des tournois:', error);
      return [];
    }
  };

  // Restaurer la session utilisateur au démarrage
  useEffect(() => {
    const bootstrapAuth = async () => {
      const storedCode = localStorage.getItem('edh_access_code') || '';
      const storedPasswordValue = localStorage.getItem('edh_access_password');

      // Guest accounts legitimately use an empty password, so only require the key to exist.
      if (!storedCode || storedPasswordValue === null) return;

      const storedPassword = storedPasswordValue;

      applyAccessCredentials(storedCode, storedPassword);
      try {
        const response = await axios.post('/api/auth/login', { code: storedCode, password: storedPassword });
        setAuthSession(response.data);
      } catch (error) {
        const status = Number(error?.response?.status || 0);

        // Keep stored credentials on transient failures (network/server cold start).
        if (status === 401 || status === 403) {
          applyAccessCredentials('', '');
          localStorage.removeItem('edh_access_code');
          localStorage.removeItem('edh_access_password');
        } else {
          console.warn('Restauration de session temporairement impossible, identifiants conserves.', error);
        }
      }
    };

    bootstrapAuth();
  }, []);

  // Charger les données après authentification
  useEffect(() => {
    if (!authSession) return;

    const loadInitialData = async () => {
      const loadedStores = await fetchStores();
      const storedStoreId = Number(localStorage.getItem('edh_selected_store_id'));
      const requestedStoreId = roundTabParams?.storeId || raceTabParams?.storeId || null;
      const authStoreIds = isSuperuser
        ? loadedStores.map((s) => s.id)
        : (authSession.permissions || []).map((p) => p.store_id);

      const initialStoreId = [requestedStoreId, storedStoreId, loadedStores[0]?.id]
        .find((id) => Number.isInteger(id) && authStoreIds.includes(id)) || null;

      if (!initialStoreId) return;

      setSelectedStoreId(initialStoreId);
      localStorage.setItem('edh_selected_store_id', String(initialStoreId));

      await fetchPlayers(initialStoreId);
      await fetchCumulativeStandings(
        initialStoreId,
        cumulativeStartDateRef.current,
        cumulativeEndDateRef.current
      );
      const loadedTournaments = await fetchTournaments(initialStoreId);

      const requestedTournamentId = roundTabParams?.tournamentId || raceTabParams?.tournamentId || null;
      if (!requestedTournamentId) return;

      const tournament = loadedTournaments.find((t) => t.id === requestedTournamentId);
      if (!tournament) return;

      setSelectedTournament(tournament);
      await fetchTournamentPlayers(tournament.id);
      const rounds = await fetchTournamentRounds(tournament.id);
      const results = await Promise.all((rounds || []).map((round) => fetchRoundResults(round.id)));
      setRoundResults(results.flat());

      if (roundTabParams) {
        const selectedRound = rounds.find((round) => round.id === roundTabParams.roundId) || null;
        setSelectedRoundView(selectedRound);
      }
    };

    loadInitialData();
  }, [roundTabParams, raceTabParams, authSession, isSuperuser, fetchCumulativeStandings]);

  useEffect(() => {
    if (!selectedStoreId || !authSession) return;
    fetchCumulativeStandings(
      selectedStoreId,
      cumulativeStartDateRef.current,
      cumulativeEndDateRef.current
    );
  }, [selectedStoreId, authSession, fetchCumulativeStandings]);

  useEffect(() => {
    if (isSuperuser) {
      fetchAccessCodes();
    }
  }, [isSuperuser, fetchAccessCodes]);

  const handleLogin = async (e) => {
    e.preventDefault();
    const code = authCodeInput.trim();
    const password = authPasswordInput;
    if (!code) return;

    setLoading(true);
    setAuthError('');
    try {
      applyAccessCredentials(code, password);
      const response = await axios.post('/api/auth/login', { code, password });
      setAuthSession(response.data);
      localStorage.setItem('edh_access_code', code);
      localStorage.setItem('edh_access_password', password);
      setAuthCodeInput('');
      setAuthPasswordInput('');
      setShowAuthPassword(false);
    } catch (error) {
      applyAccessCredentials('', '');
      setAuthError(error?.response?.data?.error || t('loginDenied'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    applyAccessCredentials('', '');
    localStorage.removeItem('edh_access_code');
    localStorage.removeItem('edh_access_password');
    localStorage.removeItem('edh_selected_store_id');

    setAuthSession(null);
    setSelectedStoreId(null);
    setStores([]);
    setPlayers([]);
    setTournaments([]);
    setSelectedTournament(null);
    setTournamentPlayers([]);
    setTournamentRounds([]);
    setRoundResults([]);
    setAccessCodes([]);
    setAuthError('');
    setShowAuthPassword(false);
    setShowAccessCodePassword(false);
  };

  const handleCreateAccessCode = async (e) => {
    e.preventDefault();
    if (!isSuperuser) return;

    setLoading(true);
    try {
      await axios.post('/api/access-codes', {
        code: newAccessCode.code.trim(),
        label: newAccessCode.label.trim(),
        password: newAccessCode.role === 'guest' ? '' : newAccessCode.password,
        role: newAccessCode.role,
        stores: newAccessCode.stores,
      });

      setNewAccessCode({ code: '', label: '', password: '', role: 'user', stores: [] });
      setShowAccessCodeForm(false);
      setShowAccessCodePassword(false);
      await fetchAccessCodes();
    } catch (error) {
      alert(error?.response?.data?.error || tr('Impossible de creer le code utilisateur.', 'Unable to create the user code.'));
    } finally {
      setLoading(false);
    }
  };

  const resetAccessCodeForm = () => {
    setNewAccessCode({ code: '', label: '', password: '', role: 'user', stores: [] });
    setEditingAccessCodeId(null);
    setShowAccessCodeForm(false);
    setShowAccessCodePassword(false);
  };

  const openEditAccessCode = (codeRow) => {
    setEditingAccessCodeId(codeRow.id);
    setNewAccessCode({
      code: codeRow.code || '',
      label: codeRow.label || '',
      password: '',
      role: codeRow.role || 'user',
      stores: Array.isArray(codeRow.stores)
        ? codeRow.stores.map((store) => ({
            store_id: Number(store.store_id),
            access_level: store.access_level === 'edit' ? 'edit' : 'read',
          }))
        : [],
    });
    setShowAccessCodeForm(true);
  };

  const handleUpdateAccessCode = async (e) => {
    e.preventDefault();
    if (!isSuperuser || !editingAccessCodeId) return;

    setLoading(true);
    try {
      await axios.put(`/api/access-codes/${editingAccessCodeId}`, {
        code: newAccessCode.code.trim(),
        label: newAccessCode.label.trim(),
        password: newAccessCode.role === 'guest' ? '' : newAccessCode.password,
        role: newAccessCode.role,
        stores: newAccessCode.role === 'superuser' ? [] : newAccessCode.stores,
      });

      resetAccessCodeForm();
      await fetchAccessCodes();
    } catch (error) {
      alert(error?.response?.data?.error || tr('Impossible de modifier le code utilisateur.', 'Unable to update the user code.'));
    } finally {
      setLoading(false);
    }
  };

  const toggleAccessCodeStore = (storeId) => {
    setNewAccessCode((prev) => {
      const exists = prev.stores.some((s) => s.store_id === storeId);
      return {
        ...prev,
        stores: exists
          ? prev.stores.filter((s) => s.store_id !== storeId)
          : [...prev.stores, { store_id: storeId, access_level: 'read' }],
      };
    });
  };

  const updateAccessCodeStoreLevel = (storeId, accessLevel) => {
    setNewAccessCode((prev) => ({
      ...prev,
      stores: prev.stores.map((s) =>
        s.store_id === storeId
          ? { ...s, access_level: prev.role === 'guest' ? 'read' : accessLevel === 'edit' ? 'edit' : 'read' }
          : s
      ),
    }));
  };

  const toggleAccessCodeActive = async (codeRow) => {
    if (!isSuperuser) return;
    try {
      await axios.put(`/api/access-codes/${codeRow.id}`, {
        is_active: !codeRow.is_active,
      });
      await fetchAccessCodes();
    } catch (error) {
      alert(error?.response?.data?.error || tr('Impossible de modifier ce code.', 'Unable to update this code.'));
    }
  };

  const deleteAccessCode = async (codeId) => {
    if (!isSuperuser) return;
    if (!window.confirm(tr('Supprimer ce code utilisateur ?', 'Delete this user code?'))) return;

    try {
      await axios.delete(`/api/access-codes/${codeId}`);
      await fetchAccessCodes();
    } catch (error) {
      alert(error?.response?.data?.error || tr('Impossible de supprimer ce code.', 'Unable to delete this code.'));
    }
  };

  const handleChangeStore = async (storeId) => {
    const nextStoreId = Number(storeId);
    if (!Number.isInteger(nextStoreId) || nextStoreId <= 0) return;

    setSelectedStoreId(nextStoreId);
    localStorage.setItem('edh_selected_store_id', String(nextStoreId));

    setSelectedTournament(null);
    setTournamentPlayers([]);
    setTournamentRounds([]);
    setRoundResults([]);
    setCumulativeStandings([]);

    await fetchPlayers(nextStoreId);
    await fetchCumulativeStandings(nextStoreId, cumulativeStartDate, cumulativeEndDate);
    await fetchTournaments(nextStoreId);
  };

  const handleApplyCumulativePeriod = async () => {
    if (!selectedStoreId) return;
    if (!cumulativeStartDate) {
      alert(tr('La date de debut est requise pour le classement cumulatif.', 'Start date is required for cumulative standings.'));
      return;
    }
    if (cumulativeEndDate && cumulativeEndDate < cumulativeStartDate) {
      alert(tr('La date de fin doit etre egale ou posterieure a la date de debut.', 'End date must be on or after the start date.'));
      return;
    }

    await fetchCumulativeStandings(selectedStoreId, cumulativeStartDate, cumulativeEndDate);
  };

  const handlePrintCumulativeStandings = () => {
    window.print();
  };

  const handleCreateStore = async (e) => {
    e.preventDefault();
    if (!newStoreName.trim()) return;

    setLoading(true);
    try {
      const response = await axios.post('/api/stores', {
        name: newStoreName.trim(),
        header_image_url: newStoreImageUrl.trim() || null,
      });
      const createdStore = response.data;
      setNewStoreName('');
      setNewStoreImageUrl('');
      setShowStoreForm(false);

      const refreshedStores = await fetchStores();
      const foundStore = refreshedStores.find((s) => s.id === createdStore.id) || createdStore;
      await handleChangeStore(foundStore.id);
    } catch (error) {
      console.error('Erreur lors de la creation du magasin:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEditStoreForm = () => {
    if (!selectedStore) return;
    setEditStoreName(selectedStore.name || '');
    setEditStoreImageUrl(selectedStore.header_image_url || '');
    setShowEditStoreForm(true);
  };

  const handleUpdateStore = async (e) => {
    e.preventDefault();
    if (!selectedStore || !editStoreName.trim()) return;

    setLoading(true);
    try {
      await axios.put(`/api/stores/${selectedStore.id}`, {
        name: editStoreName.trim(),
        header_image_url: editStoreImageUrl.trim() || null,
      });

      const refreshedStores = await fetchStores();
      const found = refreshedStores.find((store) => store.id === selectedStore.id);
      if (found) {
        setSelectedStoreId(found.id);
      }
      setShowEditStoreForm(false);
      setEditStoreName('');
      setEditStoreImageUrl('');
    } catch (error) {
      console.error('Erreur lors de la modification du magasin:', error);
      alert(error?.response?.data?.error || tr('Impossible de modifier ce magasin.', 'Unable to update this store.'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStore = async () => {
    if (!selectedStore) return;
    if (!window.confirm(tr(`Supprimer le magasin "${selectedStore.name}" ?`, `Delete store "${selectedStore.name}"?`))) return;

    setLoading(true);
    try {
      await axios.delete(`/api/stores/${selectedStore.id}`);

      const refreshedStores = await fetchStores();
      const nextStore = refreshedStores[0] || null;

      if (nextStore) {
        await handleChangeStore(nextStore.id);
      } else {
        setSelectedStoreId(null);
        localStorage.removeItem('edh_selected_store_id');
        setPlayers([]);
        setTournaments([]);
        setSelectedTournament(null);
        setTournamentPlayers([]);
        setTournamentRounds([]);
        setRoundResults([]);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du magasin:', error);
      alert(error?.response?.data?.error || tr('Impossible de supprimer ce magasin.', 'Unable to delete this store.'));
    } finally {
      setLoading(false);
    }
  };
  const handleSavePlayer = async (e) => {
    e.preventDefault();
    if (!selectedStoreId || !canEditCurrentStore) return;
    if (!validatePlayerForm()) return;
    
    setLoading(true);
    try {
      if (editingPlayer) {
        await axios.put(`/api/players/${editingPlayer.id}`, newPlayer);
        showNotification(t('playerUpdated'), 'success');
      } else {
        await axios.post('/api/players', {
          ...newPlayer,
          store_id: selectedStoreId,
        });
        showNotification(t('playerCreated'), 'success');
      }
      fetchPlayers(selectedStoreId);
      closePlayerForm();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du joueur:', error);
      showNotification(error.response?.data?.message || t('saveError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlayer = async (playerId) => {
    if (!window.confirm(t('deletePlayerConfirm'))) return;
    if (!selectedStoreId || !canEditCurrentStore) return;
    
    setLoading(true);
    try {
      await axios.delete(`/api/players/${playerId}`);
      showNotification(t('playerDeleted'), 'success');
      fetchPlayers(selectedStoreId);
    } catch (error) {
      console.error('Erreur lors de la suppression du joueur:', error);
      showNotification(error.response?.data?.message || t('deleteError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const openEditPlayerForm = (player) => {
    setEditingPlayer(player);
    setNewPlayer({ name: player.name, email: player.email, is_regular: Boolean(player.is_regular) });
    setShowPlayerForm(true);
  };

  const closePlayerForm = () => {
    setShowPlayerForm(false);
    setEditingPlayer(null);
    setNewPlayer({ name: '', email: '', is_regular: false });
  };

  const toggleRegularPlayer = async (player) => {
    if (!selectedStoreId || !canEditCurrentStore) return;

    try {
      await axios.put(`/api/players/${player.id}`, {
        is_regular: !player.is_regular,
      });
      await fetchPlayers(selectedStoreId);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut habitue:', error);
      showNotification(tr('Impossible de modifier le statut habitue.', 'Unable to update regular status.'), 'error');
    }
  };

  const handleImportPlayersCSV = async (file) => {
    if (!file || !selectedStoreId || !canEditCurrentStore) return;
    
    setImportingPlayers(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter((line) => line.trim());
      const playersToImport = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        const parts = line.split(',').map((p) => p.trim());
        if (parts.length < 2) {
          showNotification(t('lineInvalidFormat', { line: i + 1 }), 'error');
          setImportingPlayers(false);
          return;
        }
        
        const name = parts[0];
        const email = parts[1];
        
        if (!name || !email) {
          showNotification(t('lineMissingNameOrEmail', { line: i + 1 }), 'error');
          setImportingPlayers(false);
          return;
        }
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          showNotification(t('lineInvalidEmail', { line: i + 1, email }), 'error');
          setImportingPlayers(false);
          return;
        }
        
        playersToImport.push({ name, email });
      }
      
      if (playersToImport.length === 0) {
        showNotification(t('importNoValidPlayer'), 'error');
        setImportingPlayers(false);
        return;
      }
      
      // Import joueurs en batch
      for (const player of playersToImport) {
        await axios.post('/api/players', {
          ...player,
          store_id: selectedStoreId,
        });
      }
      
      showNotification(t('importPlayerSuccess', { count: playersToImport.length }), 'success');
      fetchPlayers(selectedStoreId);
      setShowImportPlayersModal(false);
    } catch (error) {
      console.error('Erreur lors de l\'import:', error);
      showNotification(error.response?.data?.message || t('importError'), 'error');
    } finally {
      setImportingPlayers(false);
    }
  };

  // Gestion des tournois
  const handleCreateTournament = async (e) => {
    e.preventDefault();
    if (!selectedStoreId || !canEditCurrentStore) return;
    if (!validateTournamentForm()) return;
    
    setLoading(true);
    try {
      const tournamentData = {
        name: newTournament.name,
        store_id: selectedStoreId,
        player_ids: newTournament.selectedPlayers
      };
      await axios.post('/api/tournaments', tournamentData);
      showNotification(t('tournamentCreated'), 'success');
      fetchTournaments(selectedStoreId);
      setShowTournamentForm(false);
      setNewTournament({ name: '', selectedPlayers: [] });
    } catch (error) {
      console.error('Erreur lors de la création du tournoi:', error);
      showNotification(error.response?.data?.message || t('tournamentCreateError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePlayerSelection = (playerId) => {
    setNewTournament(prev => ({
      ...prev,
      selectedPlayers: prev.selectedPlayers.includes(playerId)
        ? prev.selectedPlayers.filter(id => id !== playerId)
        : [...prev.selectedPlayers, playerId]
    }));
  };

  const openEditTournamentForm = () => {
    if (!selectedTournament) return;
    setEditTournament({
      name: selectedTournament.name || '',
      status: selectedTournament.status || 'pending',
      selectedPlayers: tournamentPlayers.map((p) => p.id),
    });
    setShowEditTournamentForm(true);
  };

  const handleEditTournamentPlayerSelection = (playerId) => {
    setEditTournament((prev) => ({
      ...prev,
      selectedPlayers: prev.selectedPlayers.includes(playerId)
        ? prev.selectedPlayers.filter((id) => id !== playerId)
        : [...prev.selectedPlayers, playerId],
    }));
  };

  const handleUpdateTournament = async (e) => {
    e.preventDefault();
    if (!selectedTournament || !canEditCurrentStore) return;
    setLoading(true);
    try {
      const response = await axios.put(
        `/api/tournaments/${selectedTournament.id}`,
        {
          name: editTournament.name,
          status: editTournament.status,
        }
      );

      const currentPlayerIds = tournamentPlayers.map((p) => p.id);
      const nextPlayerIds = editTournament.selectedPlayers;

      const playersToAdd = nextPlayerIds.filter((id) => !currentPlayerIds.includes(id));
      const playersToRemove = currentPlayerIds.filter((id) => !nextPlayerIds.includes(id));

      await Promise.all([
        ...playersToAdd.map((playerId) =>
          axios.post('/api/tournament-players', {
            tournament_id: selectedTournament.id,
            player_id: playerId,
          })
        ),
        ...playersToRemove.map((playerId) =>
          axios.delete(`/api/tournament-players/${selectedTournament.id}/${playerId}`)
        ),
      ]);

      setSelectedTournament(response.data);
      setTournaments((prev) =>
        prev.map((t) => (t.id === response.data.id ? response.data : t))
      );

      await fetchTournamentPlayers(selectedTournament.id);
      setShowEditTournamentForm(false);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du tournoi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTournament = async (tournamentId) => {
    if (!window.confirm(tr('Etes-vous sur de vouloir supprimer ce tournoi ? Cette action est irreversible.', 'Are you sure you want to delete this tournament? This action cannot be undone.'))) {
      return;
    }
    if (!canEditCurrentStore) return;

    setLoading(true);
    try {
      await axios.delete(`/api/tournaments/${tournamentId}`);
      setSelectedTournament(null);
      await fetchTournaments(selectedStoreId);
      setTournamentPlayers([]);
      setTournamentRounds([]);
      setRoundResults([]);
    } catch (error) {
      console.error('Erreur lors de la suppression du tournoi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTournamentClick = async (tournament) => {
    setSelectedTournament(tournament);
    await fetchTournamentPlayers(tournament.id);
    const rounds = await fetchTournamentRounds(tournament.id);
    const results = await Promise.all((rounds || []).map((round) => fetchRoundResults(round.id)));
    setRoundResults(results.flat());
  };

  const handleRefreshTournamentView = async () => {
    if (!selectedTournament || !selectedStoreId) return;

    setLoading(true);
    try {
      const refreshedTournaments = await fetchTournaments(selectedStoreId);
      const refreshedTournament =
        refreshedTournaments.find((t) => t.id === selectedTournament.id) || selectedTournament;

      setSelectedTournament(refreshedTournament);
      await fetchTournamentPlayers(refreshedTournament.id);
      const rounds = await fetchTournamentRounds(refreshedTournament.id);
      const results = await Promise.all((rounds || []).map((round) => fetchRoundResults(round.id)));
      setRoundResults(results.flat());
    } catch (error) {
      console.error('Erreur lors du rafraîchissement du tournoi:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedTournament) {
      setAbsentNextRoundIds([]);
      setDroppedPlayerIds([]);
      return;
    }

    const raw = localStorage.getItem(getPresenceStorageKey(selectedTournament.id));
    if (!raw) {
      setAbsentNextRoundIds([]);
      setDroppedPlayerIds([]);
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      const absent = Array.isArray(parsed.absentNextRoundIds) ? parsed.absentNextRoundIds : [];
      const dropped = Array.isArray(parsed.droppedPlayerIds) ? parsed.droppedPlayerIds : [];
      setAbsentNextRoundIds(absent);
      setDroppedPlayerIds(dropped);
    } catch (_error) {
      setAbsentNextRoundIds([]);
      setDroppedPlayerIds([]);
    }
  }, [selectedTournament]);

  const fetchTournamentPlayers = async (tournamentId) => {
    try {
      const response = await axios.get(`/api/tournaments/${tournamentId}/players`);
      setTournamentPlayers(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des joueurs du tournoi:', error);
    }
  };

  const fetchTournamentRounds = async (tournamentId) => {
    try {
      const response = await axios.get(`/api/tournaments/${tournamentId}/rounds`);
      setTournamentRounds(response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des rounds:', error);
      return [];
    }
  };

  useEffect(() => {
    if (!roundTabParams?.roundId) return;
    const selectedRound = tournamentRounds.find((round) => round.id === roundTabParams.roundId) || null;
    setSelectedRoundView(selectedRound);
  }, [roundTabParams, tournamentRounds]);

  useEffect(() => {
    if (!roundTabParams?.roundId || !selectedTournament?.id) return;

    let cancelled = false;

    const refreshRoundTabData = async () => {
      const rounds = await fetchTournamentRounds(selectedTournament.id);
      if (cancelled) return;

      const refreshedRoundResults = await fetchRoundResults(roundTabParams.roundId);
      if (cancelled) return;

      setRoundResults((prev) => [
        ...prev.filter((result) => result.roundId !== roundTabParams.roundId),
        ...refreshedRoundResults,
      ]);

      const selectedRound = rounds.find((round) => round.id === roundTabParams.roundId) || null;
      setSelectedRoundView(selectedRound);
    };

    refreshRoundTabData();
    const intervalId = window.setInterval(refreshRoundTabData, 2000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [roundTabParams?.roundId, selectedTournament?.id]);

  const fetchRoundResults = async (roundId) => {
    try {
      const response = await axios.get(`/api/rounds/${roundId}/results`);
      return response.data.map((tableResult) => ({
        ...tableResult,
        roundId,
      }));
    } catch (error) {
      console.error('Erreur lors du chargement des résultats:', error);
      return [];
    }
  };

  const handleAddPlayerToTournament = async (playerId) => {
    if (!selectedTournament || !canEditCurrentStore) return;
    setLoading(true);
    try {
      await axios.post('/api/tournament-players', {
        tournament_id: selectedTournament.id,
        player_id: playerId,
      });
      await fetchTournamentPlayers(selectedTournament.id);
    } catch (error) {
      console.error('Erreur lors de l\'ajout du joueur au tournoi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePlayerFromTournament = async (playerId) => {
    if (!selectedTournament || !canEditCurrentStore) return;
    setLoading(true);
    try {
      await axios.delete(`/api/tournament-players/${selectedTournament.id}/${playerId}`);
      await fetchTournamentPlayers(selectedTournament.id);
      const nextAbsent = absentNextRoundIds.filter((id) => id !== playerId);
      const nextDropped = droppedPlayerIds.filter((id) => id !== playerId);
      setAbsentNextRoundIds(nextAbsent);
      setDroppedPlayerIds(nextDropped);
      persistPresenceState(selectedTournament.id, nextAbsent, nextDropped);
    } catch (error) {
      console.error('Erreur lors de la suppression du joueur du tournoi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateRound = async () => {
    if (!selectedTournament || !canEditCurrentStore) return;
    setLoading(true);
    try {
      const excludedIds = [...new Set([...absentNextRoundIds, ...droppedPlayerIds])];
      const response = await axios.post(`/api/tournaments/${selectedTournament.id}/rounds`, {
        exclude_player_ids: excludedIds,
        duration_minutes: clampRoundTimerMinutes(roundDurationMinutes),
      });
      if (response.data?.id && response.data?.timer_backend_persisted === false) {
        writeRoundTimerFallback(response.data.id, {
          durationMinutes: clampRoundTimerMinutes(response.data.timer_duration_minutes),
          startedAt: null,
          elapsedSeconds: 0,
        });
      }
      const rounds = await fetchTournamentRounds(selectedTournament.id);
      const results = await Promise.all((rounds || []).map((round) => fetchRoundResults(round.id)));
      setRoundResults(results.flat());
      await fetchTournaments(selectedStoreId);

      setAbsentNextRoundIds([]);
      persistPresenceState(selectedTournament.id, [], droppedPlayerIds);
    } catch (error) {
      console.error('Erreur lors de la génération du round:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoundTimerAction = async (roundId, payload) => {
    if (!selectedTournament || !canEditCurrentStore) return;
    setLoading(true);
    try {
      await axios.patch(`/api/rounds/${roundId}/timer`, payload);
      await fetchTournamentRounds(selectedTournament.id);
    } catch (error) {
      const backendUnavailable = error?.response?.data?.code === 'ROUND_TIMER_UNAVAILABLE';
      if (backendUnavailable) {
        const round = tournamentRounds.find((item) => item.id === roundId) || null;
        const existing = readRoundTimerFallback(roundId);
        const nextState = {
          durationMinutes: clampRoundTimerMinutes(existing?.durationMinutes ?? round?.timer_duration_minutes ?? roundDurationMinutes),
          startedAt: existing?.startedAt || null,
          elapsedSeconds: Math.max(0, Number(existing?.elapsedSeconds) || 0),
        };

        if (payload?.duration_minutes !== undefined && payload?.duration_minutes !== null) {
          nextState.durationMinutes = clampRoundTimerMinutes(payload.duration_minutes);
        }

        if (payload?.action === 'start' && !nextState.startedAt) {
          nextState.startedAt = new Date().toISOString();
        }

        if (payload?.action === 'stop' && nextState.startedAt) {
          nextState.elapsedSeconds += Math.max(0, Math.floor((Date.now() - new Date(nextState.startedAt).getTime()) / 1000));
          nextState.startedAt = null;
        }

        writeRoundTimerFallback(roundId, nextState);
        showNotification(tr('Timer utilise en mode local tant que la migration DB n\'est pas appliquee.', 'Timer is using local mode until the DB migration is applied.'), 'info');
        return;
      }
      console.error('Erreur lors de la mise a jour du timer de ronde:', error);
      showNotification(tr('Impossible de mettre a jour le timer de ronde.', 'Unable to update the round timer.'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderRoundTimerPanel = (round, compact = false, fullscreen = false) => {
    const timerState = getRoundTimerState(round);

    return (
      <div className={`rounded-lg border ${timerState.isExpired ? 'border-red-600 bg-red-950/30' : 'border-gray-600 bg-gray-900/40'} ${fullscreen ? 'px-6 py-8 md:px-10 md:py-10' : compact ? 'px-3 py-2' : 'p-3'}`}>
        <div className={`flex ${fullscreen ? 'flex-col items-center justify-center text-center gap-3' : compact ? 'flex-col gap-2' : 'flex-col md:flex-row md:items-center md:justify-between gap-3'}`}>
          <div className={fullscreen ? 'w-full' : ''}>
            <p className={`uppercase tracking-wide text-gray-400 ${fullscreen ? 'text-sm md:text-base' : 'text-xs'}`}>{tr('Timer de ronde', 'Round timer')}</p>
            <p className={`font-mono font-bold ${fullscreen ? 'text-6xl md:text-8xl lg:text-9xl leading-none text-center' : compact ? 'text-xl' : 'text-2xl'} ${timerState.isExpired ? 'text-red-300' : 'text-cyan-300'}`}>
              {formatRoundTimerClock(timerState.remainingMs)}
            </p>
            <p className={`${fullscreen ? 'text-sm md:text-base text-gray-300 mt-3' : 'text-xs text-gray-400 mt-1'}`}>
              {tr('Duree', 'Duration')}: {timerState.durationMinutes} min · {timerState.isRunning
                ? tr('En cours', 'Running')
                : timerState.elapsedMs > 0
                ? tr('Arrete', 'Stopped')
                : tr('Pret', 'Ready')}
            </p>
            {timerState.usesFallback && (
              <p className={`${fullscreen ? 'text-xs md:text-sm text-amber-300 mt-2' : 'text-[11px] text-amber-300 mt-1'}`}>
                {tr('Mode local actif', 'Local mode active')}
              </p>
            )}
          </div>

          {canEditCurrentStore && !fullscreen && (
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => handleRoundTimerAction(round.id, { action: 'start' })}
                disabled={loading || timerState.isRunning}
                className="bg-emerald-600 hover:bg-emerald-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {timerState.elapsedMs > 0 ? tr('Reprendre', 'Resume') : tr('Demarrer', 'Start')}
              </button>
              <button
                type="button"
                onClick={() => handleRoundTimerAction(round.id, { action: 'stop' })}
                disabled={loading || !timerState.isRunning}
                className="bg-amber-600 hover:bg-amber-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {tr('Arreter', 'Stop')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleDeleteRound = async (roundId) => {
    if (!window.confirm(tr('Etes-vous sur de vouloir supprimer ce round ?', 'Are you sure you want to delete this round?'))) return;
    if (!selectedTournament || !canEditCurrentStore) return;
    setLoading(true);
    try {
      await axios.delete(`/api/rounds/${roundId}`);
      const rounds = await fetchTournamentRounds(selectedTournament.id);
      const results = await Promise.all((rounds || []).map((round) => fetchRoundResults(round.id)));
      setRoundResults(results.flat());
    } catch (error) {
      console.error('Erreur lors de la suppression du round:', error);
    } finally {
      setLoading(false);
    }
  };

  const startEditingTable = (roundId, table) => {
    const tableResults = roundResults.find(
      (tr) => tr.roundId === roundId && tr.tableNumber === table.tableNumber
    );
    const initialDraft = {};
    sortPlayersByName(table.players).forEach((player) => {
      const existingResult = tableResults?.results.find((r) => r.player.id === player.id);
      initialDraft[player.id] = {
        position: existingResult ? String(existingResult.position) : '',
        isDraw: Boolean(existingResult?.isDraw),
      };
    });

    if (selectedTournament) {
      const draftKey = getDraftStorageKey(selectedTournament.id, roundId, table.tableNumber);
      const savedDraftRaw = localStorage.getItem(draftKey);
      if (savedDraftRaw) {
        try {
          const savedDraft = JSON.parse(savedDraftRaw);
          Object.keys(savedDraft || {}).forEach((playerId) => {
            if (initialDraft[playerId]) {
              initialDraft[playerId] = {
                position: savedDraft[playerId].position || '',
                isDraw: Boolean(savedDraft[playerId].isDraw),
              };
            }
          });
        } catch (_error) {
          // Ignorer les brouillons corrompus
        }
      }
    }

    setEditingTableDraft(initialDraft);
    setEditingRoundId(roundId);
    setEditingTable(table);
  };

  const openRoundView = (round) => {
    if (!selectedTournament || !selectedStoreId) return;
    const url = buildRoundTabUrl(selectedStoreId, selectedTournament.id, round.id, false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openRoundFullscreenView = (round) => {
    if (!selectedTournament || !selectedStoreId || !round) return;
    const url = buildRoundTabUrl(selectedStoreId, selectedTournament.id, round.id, true);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const toggleAbsentNextRound = (playerId) => {
    if (!selectedTournament || droppedPlayerIds.includes(playerId)) return;
    const nextAbsent = absentNextRoundIds.includes(playerId)
      ? absentNextRoundIds.filter((id) => id !== playerId)
      : [...absentNextRoundIds, playerId];
    setAbsentNextRoundIds(nextAbsent);
    persistPresenceState(selectedTournament.id, nextAbsent, droppedPlayerIds);
  };

  const toggleDroppedPlayer = (playerId) => {
    if (!selectedTournament) return;
    const nextDropped = droppedPlayerIds.includes(playerId)
      ? droppedPlayerIds.filter((id) => id !== playerId)
      : [...droppedPlayerIds, playerId];
    const nextAbsent = absentNextRoundIds.filter((id) => id !== playerId);
    setDroppedPlayerIds(nextDropped);
    setAbsentNextRoundIds(nextAbsent);
    persistPresenceState(selectedTournament.id, nextAbsent, nextDropped);
  };

  const cancelEditingTable = () => {
    setEditingTable(null);
    setEditingRoundId(null);
    setEditingTableDraft({});
  };

  useEffect(() => {
    if (!selectedTournament || !editingTable || !editingRoundId) return;
    const draftKey = getDraftStorageKey(selectedTournament.id, editingRoundId, editingTable.tableNumber);
    localStorage.setItem(draftKey, JSON.stringify(editingTableDraft));
  }, [selectedTournament, editingTable, editingRoundId, editingTableDraft]);

  const handleSaveTableResults = async (roundId, tableNumber, results) => {
    if (!canEditCurrentStore) return;
    setLoading(true);
    try {
      await axios.post(`/api/rounds/${roundId}/tables/${tableNumber}/results`, {
        results,
      });
      const refreshedRoundResults = await fetchRoundResults(roundId);
      setRoundResults((prev) => [
        ...prev.filter((r) => r.roundId !== roundId),
        ...refreshedRoundResults,
      ]);

      if (selectedTournament) {
        const draftKey = getDraftStorageKey(selectedTournament.id, roundId, tableNumber);
        localStorage.removeItem(draftKey);
      }

      setEditingTable(null);
      setEditingRoundId(null);
      setEditingTableDraft({});
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des résultats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAvailablePlayers = () => {
    const available = players.filter((player) => !tournamentPlayers.some((tp) => tp.id === player.id));
    return sortPlayersByName(available);
  };

  const calculateTournamentResults = () => {
    if (!selectedTournament) return [];

    const playerStats = {};

    // Initialiser tous les joueurs participants
    tournamentPlayers.forEach(player => {
      playerStats[player.id] = {
        id: player.id,
        name: player.name,
        email: player.email,
        totalPoints: 0,
        roundsPlayed: 0,
        wins: 0,
        draws: 0,
      };
    });

    // Agréger les résultats réellement saisis pour refléter les draws.
    roundResults.forEach((tableResult) => {
      tableResult.results.forEach((result) => {
        const playerId = result.player.id;
        if (!playerStats[playerId]) return;

        playerStats[playerId].totalPoints += Number(result.pointsAwarded || 0);
        playerStats[playerId].roundsPlayed += 1;
        if (result.isDraw) {
          playerStats[playerId].draws += 1;
        }
        if (result.position === 1 && !result.isDraw) {
          playerStats[playerId].wins += 1;
        }
      });
    });

    const playerOpponents = {};
    Object.keys(playerStats).forEach((playerId) => {
      playerOpponents[playerId] = new Set();
    });

    tournamentRounds.forEach((round) => {
      round.tables.forEach((table) => {
        const ids = table.players.map((p) => p.id);
        ids.forEach((id) => {
          if (!playerOpponents[id]) return;
          ids.forEach((otherId) => {
            if (id !== otherId) playerOpponents[id].add(otherId);
          });
        });
      });
    });

    Object.values(playerStats).forEach((stats) => {
      const opponents = Array.from(playerOpponents[stats.id] || []);
      if (opponents.length === 0) {
        stats.oppAvgPoints = 0;
        stats.oppAvgWins = 0;
        return;
      }

      const totalOppPoints = opponents.reduce((sum, oppId) => sum + (playerStats[oppId]?.totalPoints || 0), 0);
      const totalOppWins = opponents.reduce((sum, oppId) => sum + (playerStats[oppId]?.wins || 0), 0);
      stats.oppAvgPoints = totalOppPoints / opponents.length;
      stats.oppAvgWins = totalOppWins / opponents.length;
    });

    // Trier par points, victoires, draws puis tiebreakers avancés
    return Object.values(playerStats).sort((a, b) => {
      if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;
      if (b.wins !== a.wins) return b.wins - a.wins;
      if (b.draws !== a.draws) return b.draws - a.draws;
      if (b.oppAvgPoints !== a.oppAvgPoints) return b.oppAvgPoints - a.oppAvgPoints;
      if (b.oppAvgWins !== a.oppAvgWins) return b.oppAvgWins - a.oppAvgWins;
      return a.name.localeCompare(b.name, 'fr');
    });
  };

  const calculateDashboard = () => {
    const totalRounds = tournamentRounds.length;
    const lastRound = tournamentRounds[tournamentRounds.length - 1] || null;
    const currentRoundNumber = lastRound ? lastRound.round_number : 0;
    const totalTables = tournamentRounds.reduce((sum, r) => sum + r.tables.length, 0);

    const tablesWithResults = roundResults.length;

    const tablesInLastRound = lastRound ? lastRound.tables.length : 0;
    const resultsInLastRound = lastRound
      ? roundResults.filter((tr) => tr.roundId === lastRound.id).length
      : 0;
    const tablesCompletedCurrentRound = resultsInLastRound;
    const tablesPendingCurrentRound = Math.max(0, tablesInLastRound - resultsInLastRound);

    const activePlayers = tournamentPlayers.filter(
      (p) => !droppedPlayerIds.includes(p.id)
    ).length;
    const droppedCount = droppedPlayerIds.length;
    const absentCount = absentNextRoundIds.length;

    const playersWithResultInLastRound = lastRound
      ? new Set(
          roundResults
            .filter((tr) => tr.roundId === lastRound.id)
            .flatMap((tr) => tr.results.map((r) => r.player.id))
        ).size
      : 0;
    const playersWithoutResultCurrentRound = lastRound
      ? lastRound.tables.reduce((sum, t) => sum + t.players.length, 0) - playersWithResultInLastRound
      : 0;

    const progressPercent =
      totalRounds > 0 && totalTables > 0
        ? Math.round((tablesWithResults / totalTables) * 100)
        : 0;

    return {
      totalRounds,
      currentRoundNumber,
      tablesInLastRound,
      tablesCompletedCurrentRound,
      tablesPendingCurrentRound,
      activePlayers,
      droppedCount,
      absentCount,
      playersWithoutResultCurrentRound,
      progressPercent,
    };
  };

  const launchDragonRace = () => {
    const results = calculateTournamentResults();
    if (results.length === 0) {
      alert(tr('Il faut des joueurs participants pour lancer la course.', 'You need participating players to start the race.'));
      return;
    }

    const participants = results.map((player, index) => {
      const finishRatio = 0.62 + (Math.random() * 0.34);

      return {
        id: player.id,
        name: player.name,
        laneIndex: index,
        color: DRAGON_COLORS[index % DRAGON_COLORS.length],
        finishTimeMs: Math.max(1200, raceDurationMs * finishRatio),
        catchupBonusMs: 0,
        finishedAtMs: null,
      };
    });

    setRaceParticipants(participants);
    setRaceElapsedMs(0);
    setRaceStartedAt(null);
    setRaceIsRunning(false);
    setRaceGoFlash(false);
    setRaceWinner(null);
    raceWinnerRef.current = null;
    setRaceCountdown(3);
  };

  const resetDragonRace = () => {
    setRaceIsRunning(false);
    setRaceStartedAt(null);
    setRaceElapsedMs(0);
    setRaceParticipants([]);
    setRaceCountdown(null);
    setRaceGoFlash(false);
    setRaceWinner(null);
    raceWinnerRef.current = null;
  };

  useEffect(() => {
    if (raceCountdown === null) return;

    if (raceCountdown <= 0) {
      setRaceCountdown(null);
      setRaceGoFlash(true);
      setRaceStartedAt(Date.now());
      setRaceIsRunning(true);
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setRaceCountdown((prev) => (prev === null ? null : prev - 1));
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [raceCountdown]);

  useEffect(() => {
    if (!raceGoFlash) return;

    const goTimerId = window.setTimeout(() => {
      setRaceGoFlash(false);
    }, 800);

    return () => window.clearTimeout(goTimerId);
  }, [raceGoFlash]);

  useEffect(() => {
    if (!raceIsRunning || !raceStartedAt) return;

    raceWinnerRef.current = null;

    const intervalId = window.setInterval(() => {
      const elapsed = Date.now() - raceStartedAt;
      const effectiveElapsed = Math.min(elapsed, raceDurationMs);
      let stopAtMs = null;

      setRaceParticipants((prev) => {
        if (prev.length === 0) return prev;
        const progresses = prev.map((d) => getDragonProgressPercent(d, effectiveElapsed));
        const leaderProg = Math.max(...progresses, 1);
        let nextWinner = null;

        // Rubber-banding: adaptive catch-up gives stronger boosts to dragons far behind.
        const updated = prev.map((dragon, idx) => {
          const hasFinishedNow = progresses[idx] >= 100;
          if (hasFinishedNow && dragon.finishedAtMs === null) {
            const finishedDragon = {
              ...dragon,
              finishedAtMs: effectiveElapsed,
            };

            if (!nextWinner || finishedDragon.finishedAtMs < nextWinner.finishedAtMs) {
              nextWinner = finishedDragon;
            }

            return finishedDragon;
          }

          if (hasFinishedNow) return dragon;
          if (elapsed >= raceDurationMs) return dragon;
          const gap = (leaderProg - progresses[idx]) / 100;
          const increment = getAdaptiveCatchupIncrementMs(gap, progresses[idx], raceTickMs);
          if (increment <= 0) return dragon;
          const maxCatchupRatio = raceCatchupMode === 'arcade'
            ? (raceCatchupIntensity === 'high' ? 0.62 : raceCatchupIntensity === 'low' ? 0.46 : 0.54)
            : (raceCatchupIntensity === 'high' ? 0.48 : raceCatchupIntensity === 'low' ? 0.34 : 0.42);
          const maxCatchupBonusMs = raceDurationMs * maxCatchupRatio;
          const nextCatchupBonus = Math.min(maxCatchupBonusMs, (dragon.catchupBonusMs || 0) + increment);
          return { ...dragon, catchupBonusMs: nextCatchupBonus };
        });

        if (!raceWinnerRef.current && nextWinner) {
          raceWinnerRef.current = nextWinner;
          setRaceWinner(nextWinner);
          stopAtMs = nextWinner.finishedAtMs;
        }

        return updated;
      });

      if (stopAtMs !== null) {
        setRaceElapsedMs(stopAtMs);
        setRaceIsRunning(false);
        return;
      }

      if (elapsed >= raceDurationMs) {
        setRaceElapsedMs(raceDurationMs);
        setRaceIsRunning(false);
        return;
      }

      setRaceElapsedMs(elapsed);
    }, raceTickMs);

    return () => window.clearInterval(intervalId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raceIsRunning, raceStartedAt, raceDurationMs, raceTickMs]);

  useEffect(() => {
    if (!raceWinner || !raceIsRunning) return;

    if (Number.isFinite(raceWinner.finishedAtMs)) {
      setRaceElapsedMs(raceWinner.finishedAtMs);
    }
    setRaceIsRunning(false);
  }, [raceWinner, raceIsRunning]);

  useEffect(() => {
    resetDragonRace();
  }, [selectedTournament?.id]);

  // BroadcastChannel: sync race state from main tab to TV tab
  useEffect(() => {
    const channel = new BroadcastChannel('edh_race_state');
    raceBroadcastChannelRef.current = channel;

    if (raceTabParams?.tv) {
      channel.onmessage = (event) => {
        if (event.data?.type !== 'race_state') return;
        const {
          raceParticipants: p,
          raceElapsedMs: e,
          raceIsRunning: r,
          raceWinner: w,
          raceCountdown: c,
          raceGoFlash: g,
          raceDurationMinutes: d,
          raceTvUltraFluid: u,
        } = event.data;
        setRaceParticipants(p);
        setRaceElapsedMs(e);
        setRaceIsRunning(r);
        setRaceWinner(w);
        setRaceCountdown(c);
        setRaceGoFlash(g);
        setRaceDurationMinutes(d);
        setRaceTvUltraFluid(Boolean(u));
      };
    }

    return () => {
      channel.close();
      raceBroadcastChannelRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // BroadcastChannel: main tab broadcasts its state so TV tab can mirror it
  useEffect(() => {
    if (raceTabParams?.tv) return;
    const channel = raceBroadcastChannelRef.current;
    if (!channel) return;
    channel.postMessage({
      type: 'race_state',
      raceParticipants,
      raceElapsedMs,
      raceIsRunning,
      raceWinner,
      raceCountdown,
      raceGoFlash,
      raceDurationMinutes,
      raceTvUltraFluid,
    });
  }, [raceParticipants, raceElapsedMs, raceIsRunning, raceWinner, raceCountdown, raceGoFlash, raceDurationMinutes, raceTvUltraFluid, raceTabParams]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem('edh_players_regular_only', showRegularPlayersOnly ? '1' : '0');
    } catch {
      // Ignore sessionStorage failures (private mode, blocked storage)
    }
  }, [showRegularPlayersOnly]);

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight || 900);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderDragonRaceSection = (fullscreenMode = false, tabMode = false, tvMode = false) => {
    const ultraFluidModeActive = tvMode && raceTvUltraFluid;
    const participantCount = raceParticipants.length;
    const compactRace = fullscreenMode && participantCount > 9;
    const regularModeHeight = participantCount > 0
      ? Math.max(320, Math.min(620, (participantCount * 36) + 120, viewportHeight - 340))
      : 320;
    const arenaHeight = tabMode
      ? Math.max(360, viewportHeight - 260)
      : fullscreenMode
      ? Math.max(320, viewportHeight - 420)
      : regularModeHeight;

    const raceComputed = raceParticipants
      .map((dragon) => {
        const boost = raceIsRunning ? getDragonBoost(dragon.id, raceElapsedMs) : { isActive: false, multiplier: 1, extraElapsedMs: 0 };
        const progress = getDragonProgressPercent(dragon, raceElapsedMs);
        return {
          ...dragon,
          progress,
          boostActive: boost.isActive,
          boostMultiplier: boost.multiplier,
          finished: progress >= 100,
        };
      });

    const raceLeaderboard = [...raceComputed].sort((a, b) => {
      const aFinished = Number.isFinite(a.finishedAtMs);
      const bFinished = Number.isFinite(b.finishedAtMs);

      if (aFinished && bFinished) {
        if (a.finishedAtMs !== b.finishedAtMs) return a.finishedAtMs - b.finishedAtMs;
        return b.progress - a.progress;
      }

      if (aFinished) return -1;
      if (bFinished) return 1;
      return b.progress - a.progress;
    });
    const rankByDragonId = new Map(raceLeaderboard.map((dragon, index) => [dragon.id, index + 1]));
    const raceTrackParticipants = [...raceComputed]
      .map((dragon) => ({
        ...dragon,
        rank: rankByDragonId.get(dragon.id) || raceComputed.length,
        isWinner: !!(raceWinner && dragon.id === raceWinner.id),
      }))
      .sort((a, b) => (a.laneIndex || 0) - (b.laneIndex || 0));

    const sectionContent = (
      <div className={`${fullscreenMode ? 'p-3 md:p-6' : ''}`}>
        <div className={`dragon-race-shell bg-gray-700 rounded-lg relative overflow-hidden ${fullscreenMode ? 'h-full p-3 md:p-4' : 'p-4'} ${ultraFluidModeActive ? 'dragon-race-tv-ultra' : ''}`}>
          <div className="dragon-race-stars" />
          {((!fullscreenMode || tabMode) && !tvMode) && (
            <>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <div className="relative z-10">
                  <h3 className="text-lg font-semibold">{tr('Course des dragons', 'Dragon race')}</h3>
                  <p className="text-gray-400 text-sm">{tr('Chaque joueur devient un dragon colore sur une piste visuelle.', 'Each player becomes a colored dragon on a visual track.')}</p>
                </div>
                <div className="flex flex-wrap items-center gap-3 relative z-10">
                  <div className="flex items-center gap-2">
                    <label htmlFor="race-minutes" className="text-sm text-gray-300">{tr('Duree (minutes)', 'Duration (minutes)')}</label>
                    <input
                      id="race-minutes"
                      type="number"
                      min="1"
                      max="180"
                      step="1"
                      value={raceDurationMinutes}
                      onChange={(e) => setRaceDurationMinutes(Math.max(1, Math.min(180, Number(e.target.value) || 1)))}
                      disabled={raceIsRunning || raceCountdown !== null}
                      className="w-20 px-2 py-1 bg-gray-800 border border-gray-600 rounded"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label htmlFor="race-catchup-mode" className="text-sm text-gray-300">{tr('Rattrapage', 'Catch-up')}</label>
                    <select
                      id="race-catchup-mode"
                      value={raceCatchupMode}
                      onChange={(e) => setRaceCatchupMode(e.target.value === 'arcade' ? 'arcade' : 'balanced')}
                      disabled={raceIsRunning || raceCountdown !== null}
                      className="px-2 py-1 bg-gray-800 border border-gray-600 rounded text-sm"
                    >
                      <option value="balanced">{tr('Equilibre', 'Balanced')}</option>
                      <option value="arcade">{tr('Arcade', 'Arcade')}</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label htmlFor="race-catchup-intensity" className="text-sm text-gray-300">{tr('Intensite', 'Intensity')}</label>
                    <select
                      id="race-catchup-intensity"
                      value={raceCatchupIntensity}
                      onChange={(e) => setRaceCatchupIntensity(['low', 'normal', 'high'].includes(e.target.value) ? e.target.value : 'normal')}
                      disabled={raceIsRunning || raceCountdown !== null}
                      className="px-2 py-1 bg-gray-800 border border-gray-600 rounded text-sm"
                    >
                      <option value="low">{tr('Faible', 'Low')}</option>
                      <option value="normal">{tr('Normal', 'Normal')}</option>
                      <option value="high">{tr('Elevee', 'High')}</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      checked={raceTvUltraFluid}
                      onChange={(e) => setRaceTvUltraFluid(e.target.checked)}
                      className="w-4 h-4 accent-cyan-500"
                    />
                    <span>{tr('Mode TV ultra-fluide', 'Ultra-smooth TV mode')}</span>
                  </label>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-4 relative z-10">
                <button
                  type="button"
                  onClick={launchDragonRace}
                  disabled={raceIsRunning || raceCountdown !== null || tournamentPlayers.length === 0}
                  className="bg-fuchsia-600 hover:bg-fuchsia-700 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {raceCountdown !== null
                    ? tr(`Depart dans ${raceCountdown}...`, `Starting in ${raceCountdown}...`)
                    : raceIsRunning
                    ? tr('Course en cours...', 'Race in progress...')
                    : tr('Lancer la course', 'Start race')}
                </button>
                <button
                  type="button"
                  onClick={resetDragonRace}
                  disabled={(raceIsRunning && raceElapsedMs < 1000) || raceCountdown !== null}
                  className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {tr('Reinitialiser', 'Reset')}
                </button>
                {!tabMode && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        if (!selectedStoreId || !selectedTournament?.id) return;
                        window.open(buildRaceTabUrl(selectedStoreId, selectedTournament.id), '_blank', 'noopener,noreferrer');
                      }}
                      className="bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      {tr('Plein ecran (nouvel onglet)', 'Fullscreen (new tab)')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!selectedStoreId || !selectedTournament?.id) return;
                        window.open(buildRaceTabUrl(selectedStoreId, selectedTournament.id, true, raceTvUltraFluid), '_blank', 'noopener,noreferrer');
                      }}
                      className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      {tr('TV mode (nouvel onglet)', 'TV mode (new tab)')}
                    </button>
                  </>
                )}
                <span className="text-sm text-cyan-300 font-semibold">
                  {tr('Temps', 'Time')}: {formatRaceClock(raceElapsedMs)} / {formatRaceClock(raceDurationMs)}
                </span>
              </div>
            </>
          )}

          {tvMode && (
            <div className="relative z-10 mb-4 flex items-center justify-between bg-gray-900/70 border border-gray-700 rounded-lg px-4 py-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-cyan-300">{tr('Mode TV', 'TV mode')}</p>
                <p className="text-base font-semibold text-white">{selectedTournament?.name || ''}</p>
                <p className="text-xs text-gray-300 mt-1">
                  {raceTvUltraFluid
                    ? tr('Ultra-fluide actif', 'Ultra-smooth enabled')
                    : tr('Ultra-fluide desactive', 'Ultra-smooth disabled')}
                </p>
              </div>
              <p className="text-xl font-mono font-bold text-cyan-300">{formatRaceClock(raceElapsedMs)}</p>
            </div>
          )}

          {raceParticipants.length > 0 ? (
            <>
              <div className={`grid gap-4 ${(fullscreenMode || tvMode) ? 'lg:grid-cols-5' : 'grid-cols-1 lg:grid-cols-3 h-full'}`}>
                <div className={`bg-gray-800/90 border border-gray-600 rounded-lg p-3 relative z-10 ${fullscreenMode ? 'h-full overflow-hidden lg:col-span-2' : 'h-fit lg:col-span-1'}`}>
                  <h4 className="text-sm uppercase tracking-wide text-cyan-300 font-semibold mb-3">{tr('Classement live', 'Live standings')}</h4>
                  <div className={`space-y-2 ${fullscreenMode ? 'max-h-[calc(100%-28px)] overflow-hidden' : ''}`}>
                    {raceLeaderboard.map((dragon, index) => (
                      <div key={dragon.id} className={`dragon-leader-row flex items-center justify-between bg-gray-700 rounded ${compactRace ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'}`}>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-200">#{index + 1}</span>
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: dragon.color }} />
                          <span className="text-gray-200 truncate max-w-[140px]">{dragon.name}</span>
                        </div>
                        <span className="text-cyan-300 font-semibold">{Math.round(dragon.progress)}%</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={`${(fullscreenMode || tvMode) ? 'h-full lg:col-span-3' : 'lg:col-span-2'} bg-gray-800/90 border border-gray-600 rounded-lg p-3 relative z-10 overflow-hidden flex flex-col`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm uppercase tracking-wide text-orange-300 font-semibold">{tr('Arene epique', 'Epic arena')}</h4>
                    <p className="text-xs text-gray-400">{tr('Vue avancee de tous les dragons', 'Advanced view of all dragons')}</p>
                  </div>

                  <div className="dragon-arena-shell relative rounded-lg border border-gray-700 bg-gray-900/80 overflow-hidden flex-1 min-h-[320px]" style={{ height: arenaHeight }}>
                    <DragonRaceArena
                      participants={raceTrackParticipants}
                      elapsedMs={raceElapsedMs}
                      fullscreenMode={fullscreenMode}
                      ultraFluidMode={ultraFluidModeActive}
                      height={arenaHeight}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <p className="text-gray-400 text-sm">{tr('Lance la course pour afficher la piste des dragons.', 'Start the race to display the dragon track.')}</p>
          )}

          {(raceCountdown !== null || raceGoFlash) && (
            <div className="race-countdown-overlay">
              <span className="race-countdown-number">{raceGoFlash ? 'GO!' : raceCountdown}</span>
            </div>
          )}

          {raceWinner && !raceIsRunning && raceParticipants.length > 0 && (
            <div className="race-winner-overlay">
              <div className="race-winner-banner" style={{ '--winner-color': raceWinner.color }}>
                <span className="race-winner-trophy">🏆</span>
                <span className="race-winner-name" style={{ color: raceWinner.color }}>{raceWinner.name}</span>
                <span className="race-winner-label">{tr('remporte la course !', 'wins the race!')}</span>
                <span className="race-winner-dragon">🐉</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );

    if (!fullscreenMode) {
      return <div className="mt-6">{sectionContent}</div>;
    }

    if (tabMode) {
      return (
        <div className="min-h-screen bg-gray-950">
          {!tvMode && (
            <div className="bg-gray-900/95 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">{tr('Course des dragons', 'Dragon race')}</p>
                <p className="text-sm text-gray-300">{selectedTournament?.name || ''}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  window.location.href = '/';
                }}
                className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {tr('Retour accueil', 'Back home')}
              </button>
            </div>
          )}
          <div className="p-4">{sectionContent}</div>
        </div>
      );
    }

    return (
      <div className="fixed inset-0 z-[70] bg-gray-950/95">
        {/* Top Bar avec Horloge & Timer */}
        <div className="absolute top-0 left-0 right-0 bg-gray-900/95 border-b border-gray-700 px-6 py-4 flex items-center justify-between z-50">
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-xs text-gray-400 mb-1">{language === 'en' ? 'TIME' : 'HEURE'}</p>
              <p className="text-2xl font-bold font-mono">
                {currentTime.toLocaleTimeString(locale)}
              </p>
            </div>
            <div className="border-l border-gray-700 h-12" />
            <div className="text-center">
              <p className="text-xs text-gray-400 mb-1">{language === 'en' ? 'ELAPSED' : 'TEMPS ECOULE'}</p>
              <p className="text-2xl font-bold font-mono text-cyan-300">
                {formatRaceClock(raceElapsedMs)}
              </p>
            </div>
            <div className="border-l border-gray-700 h-12" />
            <div className="text-center">
              <p className="text-xs text-gray-400 mb-1">{language === 'en' ? 'TOTAL DURATION' : 'DUREE TOTALE'}</p>
              <p className="text-2xl font-bold font-mono text-amber-400">
                {formatRaceClock(raceDurationMs)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              window.location.href = '/';
            }}
            className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            {tr('Retour accueil', 'Back home')}
          </button>
        </div>
        
        {/* Content avec padding pour la barre */}
        <div className="pt-24 px-4 pb-4" style={{ height: 'calc(100% - 96px)', overflowY: 'auto' }}>
          {sectionContent}
        </div>
      </div>
    );
  };

  if (authSession && raceTabParams) {
    return selectedTournament && selectedStoreId
      ? renderDragonRaceSection(true, true, Boolean(raceTabParams.tv))
      : (
        <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center p-6">
          <p className="text-gray-300 text-lg">{tr('Chargement de la course...', 'Loading race...')}</p>
        </div>
      );
  }

  return (
    <div className={`min-h-screen bg-gray-900 text-white app-shell ${roundTabParams?.fullscreen ? '' : 'p-4'}`}>
      {!roundTabParams?.fullscreen && (
        <button
          type="button"
          onClick={() => setShowHelpModal(true)}
          className="fixed top-4 right-4 z-50 w-10 h-10 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-xl shadow-lg transition-colors no-print"
          aria-label={t('helpAriaLabel')}
          title={t('helpTitle')}
        >
          ?
        </button>
      )}

      {!roundTabParams?.fullscreen && (
        <div className="fixed top-4 left-4 z-50 no-print">
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value === 'en' ? 'en' : 'fr')}
            className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500"
            aria-label="Language selector"
          >
            <option value="fr">FR</option>
            <option value="en">EN</option>
          </select>
        </div>
      )}

      {!authSession && (
        <div className="max-w-md mx-auto mt-16 bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-2xl font-semibold mb-3">{t('loginTitle')}</h2>
          <p className="text-gray-400 text-sm mb-4">{t('loginSubtitle')}</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="text"
              value={authCodeInput}
              onChange={(e) => setAuthCodeInput(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
              placeholder={t('userCodePlaceholder')}
              required
            />
            <div className="relative">
              <input
                type={showAuthPassword ? 'text' : 'password'}
                value={authPasswordInput}
                onChange={(e) => setAuthPasswordInput(e.target.value)}
                className="w-full pr-24 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                placeholder={t('passwordPlaceholder')}
              />
              <button
                type="button"
                onClick={() => setShowAuthPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 px-3 text-sm text-cyan-300 hover:text-cyan-200"
              >
                {showAuthPassword ? tr('Masquer', 'Hide') : tr('Afficher', 'Show')}
              </button>
            </div>
            {authError && <p className="text-red-400 text-sm">{authError}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-cyan-600 hover:bg-cyan-700 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {loading ? t('loginLoading') : t('loginButton')}
            </button>
          </form>
        </div>
      )}

      {authSession && (

      <div className={`${roundTabParams?.fullscreen ? 'w-full min-h-screen' : 'max-w-6xl mx-auto space-y-8'}`}>
        {!roundTabParams?.fullscreen && selectedStore?.header_image_url && (
          <div className="relative overflow-hidden rounded-2xl border border-gray-700">
            <img
              src={selectedStore.header_image_url}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover blur-md scale-105 opacity-40"
            />
            <img
              src={selectedStore.header_image_url}
              alt={selectedStore.name}
              className="relative w-full h-48 md:h-64 object-contain"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <p className="text-xs uppercase tracking-[0.25em] text-cyan-300">{t('activeStoreBadge')}</p>
              <h2 className="text-3xl font-bold text-white mt-1">{selectedStore.name}</h2>
            </div>
          </div>
        )}

        {!roundTabParams?.fullscreen && !hasStoreBanner && (
          <>
            <h1 className="text-4xl font-bold text-center">EDH Manager</h1>
            <p className="text-center text-gray-400">{t('appSubtitle')}</p>
          </>
        )}
        {!roundTabParams?.fullscreen && (
          <div className="bg-gray-800 rounded-lg p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <p className="text-sm text-gray-300">
              {t('connectedAs', {
                label: authSession.label || authSession.role,
                role: authSession.role === 'superuser' ? t('superuser') : t('user'),
              })}
            </p>
            <button
              type="button"
              onClick={handleLogout}
              className="bg-gray-600 hover:bg-gray-500 px-3 py-1 rounded text-sm transition-colors"
            >
              {t('logout')}
            </button>
          </div>
        )}

        {!roundTabParams && (
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-semibold">{t('storesTitle')}</h2>
                <p className="text-gray-400 text-sm">{t('storesSubtitle')}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowStoreForm(true)}
                disabled={!isSuperuser}
                className="bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {t('createStore')}
              </button>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium mb-2">{t('activeStoreLabel')}</label>
              <div className="flex flex-col md:flex-row gap-3 md:items-center">
                <select
                  value={selectedStoreId || ''}
                  onChange={(e) => handleChangeStore(e.target.value)}
                  className="w-full md:w-96 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-emerald-500"
                >
                  <option value="">{t('selectStorePlaceholder')}</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </select>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={openEditStoreForm}
                    disabled={!selectedStoreId || !canEditCurrentStore}
                    className="bg-yellow-600 hover:bg-yellow-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {t('edit')}
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteStore}
                    disabled={!selectedStoreId || loading || !isSuperuser}
                    className="bg-red-700 hover:bg-red-800 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {t('delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!roundTabParams && !selectedStoreId && (
          <div className="bg-yellow-900/40 border border-yellow-700 rounded-lg p-4 text-yellow-100">
            {tr('Selectionne ou cree un magasin pour acceder aux joueurs et aux tournois.', 'Select or create a store to access players and tournaments.')}
          </div>
        )}

        {!roundTabParams && isSuperuser && (
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-semibold">{tr('Codes utilisateurs', 'User codes')}</h2>
                <p className="text-gray-400 text-sm">{tr('Cree des codes avec acces lecture ou edition par magasin.', 'Create codes with read or edit access per store.')}</p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setAccessCodesCollapsed((prev) => !prev)}
                  className="px-3 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-sm transition-colors"
                >
                  {accessCodesCollapsed ? '▼' : '▲'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (showAccessCodeForm) {
                      resetAccessCodeForm();
                    } else {
                      setShowAccessCodeForm(true);
                      setEditingAccessCodeId(null);
                    }
                  }}
                  className="bg-cyan-600 hover:bg-cyan-700 px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  {showAccessCodeForm ? tr('Fermer', 'Close') : tr('+ Nouveau code', '+ New code')}
                </button>
              </div>
            </div>

            {!accessCodesCollapsed && showAccessCodeForm && (
              <form onSubmit={editingAccessCodeId ? handleUpdateAccessCode : handleCreateAccessCode} className="bg-gray-700 rounded-lg p-4 mb-4 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-lg font-semibold">
                    {editingAccessCodeId ? tr('Modifier le code utilisateur', 'Edit user code') : tr('Creer un code utilisateur', 'Create user code')}
                  </h3>
                  {editingAccessCodeId && (
                    <button
                      type="button"
                      onClick={resetAccessCodeForm}
                      className="bg-gray-600 hover:bg-gray-500 px-3 py-1 rounded text-sm"
                    >
                      {tr('Annuler l\'edition', 'Cancel editing')}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    value={newAccessCode.label}
                    onChange={(e) => setNewAccessCode((prev) => ({ ...prev, label: e.target.value }))}
                    className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg"
                    placeholder="Label (ex: TO Montreal)"
                    required
                  />
                  <input
                    type="text"
                    value={newAccessCode.code}
                    onChange={(e) => setNewAccessCode((prev) => ({ ...prev, code: e.target.value }))}
                    className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg"
                    placeholder="Code (ex: TO-MTL-001)"
                    required
                  />
                  <div className="relative">
                    <input
                      type={showAccessCodePassword ? 'text' : 'password'}
                      value={newAccessCode.password}
                      onChange={(e) => setNewAccessCode((prev) => ({ ...prev, password: e.target.value }))}
                      className="w-full pr-24 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg"
                      placeholder={newAccessCode.role === 'guest'
                        ? tr('Aucun mot de passe requis pour invite', 'No password required for guest')
                        : editingAccessCodeId
                        ? tr('Nouveau mot de passe (optionnel)', 'New password (optional)')
                        : tr('Mot de passe', 'Password')}
                      required={!editingAccessCodeId && newAccessCode.role !== 'guest'}
                      disabled={newAccessCode.role === 'guest'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowAccessCodePassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 px-3 text-sm text-cyan-300 hover:text-cyan-200 disabled:opacity-50"
                      disabled={newAccessCode.role === 'guest'}
                    >
                      {showAccessCodePassword ? tr('Masquer', 'Hide') : tr('Afficher', 'Show')}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
                  <select
                    value={newAccessCode.role}
                    onChange={(e) => setNewAccessCode((prev) => ({ ...prev, role: e.target.value, password: e.target.value === 'guest' ? '' : prev.password, stores: prev.stores.map((store) => ({ ...store, access_level: e.target.value === 'guest' ? 'read' : store.access_level })) }))}
                    className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg"
                  >
                    <option value="user">{tr('Utilisateur', 'User')}</option>
                    <option value="guest">{tr('Invite', 'Guest')}</option>
                    <option value="superuser">Superuser</option>
                  </select>
                </div>

                {editingAccessCodeId && (
                  <p className="text-xs text-gray-400">
                    {tr('Laisse le champ mot de passe vide si tu ne veux pas le modifier.', 'Leave the password field empty if you do not want to change it.')}
                  </p>
                )}

                {newAccessCode.role !== 'superuser' && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-300">{tr('Magasins autorises', 'Allowed stores')}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {stores.map((store) => {
                        const selected = newAccessCode.stores.find((s) => s.store_id === store.id);
                        return (
                          <div key={store.id} className="bg-gray-800 p-3 rounded-lg border border-gray-600">
                            <label className="flex items-center gap-2 mb-2">
                              <input
                                type="checkbox"
                                checked={Boolean(selected)}
                                onChange={() => toggleAccessCodeStore(store.id)}
                              />
                              <span>{store.name}</span>
                            </label>
                            {selected && (
                              <select
                                value={selected.access_level}
                                onChange={(e) => updateAccessCodeStoreLevel(store.id, e.target.value)}
                                disabled={newAccessCode.role === 'guest'}
                                className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded disabled:opacity-50"
                              >
                                <option value="read">{tr('Lecture', 'Read')}</option>
                                <option value="edit">{tr('Edition', 'Edit')}</option>
                              </select>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="bg-cyan-600 hover:bg-cyan-700 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {loading
                    ? (editingAccessCodeId ? tr('Enregistrement...', 'Saving...') : tr('Creation...', 'Creating...'))
                    : (editingAccessCodeId ? tr('Enregistrer les modifications', 'Save changes') : tr('Creer le code', 'Create code'))}
                </button>
              </form>
            )}

            {!accessCodesCollapsed && (
              <div className="space-y-2">
                {accessCodes.map((codeRow) => (
                  <div key={codeRow.id} className="bg-gray-700 p-3 rounded-lg flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div>
                      <p className="font-semibold">{codeRow.label} - {codeRow.code}</p>
                      <p className="text-sm text-gray-400">
                        {codeRow.role === 'superuser' ? 'Superuser' : codeRow.role === 'guest' ? tr('Invite', 'Guest') : tr('Utilisateur', 'User')} - {codeRow.is_active ? tr('Actif', 'Active') : tr('Inactif', 'Inactive')}
                      </p>
                      {codeRow.role !== 'superuser' && Array.isArray(codeRow.stores) && codeRow.stores.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {codeRow.stores
                            .map((storeAccess) => {
                              const store = stores.find((s) => s.id === Number(storeAccess.store_id));
                              const storeName = store?.name || tr(`Magasin ${storeAccess.store_id}`, `Store ${storeAccess.store_id}`);
                              return `${storeName} (${storeAccess.access_level === 'edit' ? tr('edition', 'edit') : tr('lecture', 'read')})`;
                            })
                            .join(' · ')}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => openEditAccessCode(codeRow)}
                        className="bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded text-sm"
                      >
                        {tr('Editer', 'Edit')}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleAccessCodeActive(codeRow)}
                        className="bg-yellow-600 hover:bg-yellow-700 px-3 py-1 rounded text-sm"
                      >
                        {codeRow.is_active ? tr('Desactiver', 'Disable') : tr('Activer', 'Enable')}
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteAccessCode(codeRow.id)}
                        className="bg-red-700 hover:bg-red-800 px-3 py-1 rounded text-sm"
                      >
                        {t('delete')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!roundTabParams && selectedStoreId && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-2xl font-semibold mb-3">{tr('Recherche de cartes', 'Card search')}</h2>
            <p className="text-gray-400 text-sm mb-4">{tr('Rechercher rapidement une carte sur FacetoFace Games.', 'Quickly search for a card on FacetoFace Games.')}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={cardSearchQuery}
                onChange={(e) => setCardSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    openCardSearch();
                  }
                }}
                className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                placeholder="Ex: Sol Ring, Rhystic Study..."
              />
              <button
                onClick={openCardSearch}
                className="bg-cyan-600 hover:bg-cyan-700 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                {tr('Rechercher', 'Search')}
              </button>
            </div>
          </div>
        )}

        {!roundTabParams && selectedStoreId && (
          <div className="bg-gray-800 rounded-lg p-6 print-cumulative-area">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-2xl font-semibold">{tr('Classement cumulatif global', 'Global cumulative standings')}</h2>
              <p className="text-xs text-gray-400">{tr('Par magasin', 'Per store')}</p>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              {tr('Classement base sur la periode choisie, avec points totaux, victoires, 2e, 3e, 4e et draws.', 'Ranking based on the selected period with total points, wins, 2nd, 3rd, 4th and draws.')}
            </p>

            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-4 no-print">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full lg:w-auto">
                <label className="text-sm text-gray-200 flex flex-col gap-1">
                  <span>{tr('Date de debut', 'Start date')}</span>
                  <input
                    type="date"
                    value={cumulativeStartDate}
                    onChange={(e) => setCumulativeStartDate(e.target.value)}
                    className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                    required
                  />
                </label>
                <label className="text-sm text-gray-200 flex flex-col gap-1">
                  <span>{tr('Date de fin (optionnelle)', 'End date (optional)')}</span>
                  <input
                    type="date"
                    value={cumulativeEndDate}
                    onChange={(e) => setCumulativeEndDate(e.target.value)}
                    className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                  />
                </label>
                <button
                  type="button"
                  onClick={handleApplyCumulativePeriod}
                  className="bg-cyan-600 hover:bg-cyan-700 px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  {tr('Appliquer la periode', 'Apply period')}
                </button>
              </div>

              <button
                type="button"
                onClick={handlePrintCumulativeStandings}
                className="bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                {tr('Imprimer / PDF', 'Print / PDF')}
              </button>
            </div>

            <p className="text-xs text-gray-400 mb-3">
              {cumulativeEndDate
                ? tr(
                    `Periode: du ${cumulativeStartDate} au ${cumulativeEndDate}`,
                    `Period: from ${cumulativeStartDate} to ${cumulativeEndDate}`
                  )
                : tr(
                    `Periode: a partir du ${cumulativeStartDate}`,
                    `Period: starting ${cumulativeStartDate}`
                  )}
            </p>

            {globalCumulativeStandings.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-2 px-3">#</th>
                      <th className="text-left py-2 px-3">{tr('Joueur', 'Player')}</th>
                      <th className="text-center py-2 px-3">{tr('Pts total', 'Total pts')}</th>
                      <th className="text-center py-2 px-3">{tr('Victoires', 'Wins')}</th>
                      <th className="text-center py-2 px-3">2e</th>
                      <th className="text-center py-2 px-3">3e</th>
                      <th className="text-center py-2 px-3">4e</th>
                      <th className="text-center py-2 px-3">Draw</th>
                    </tr>
                  </thead>
                  <tbody>
                    {globalCumulativeStandings.map((player, index) => (
                      <tr key={player.id} className="border-b border-gray-700/70">
                        <td className="py-2 px-3 font-semibold text-cyan-300">#{index + 1}</td>
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            <span>{player.name}</span>
                            {player.is_regular && (
                              <span className="text-[10px] bg-cyan-800 text-cyan-100 px-2 py-0.5 rounded-full uppercase tracking-wide">
                                {tr('Habitue', 'Regular')}
                              </span>
                            )}
                          </div>
                          {getPlayerBadges(player).length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {getPlayerBadges(player).map((badge) => (
                                <span
                                  key={badge.key}
                                  className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide ${badge.className}`}
                                >
                                  {badge.label}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-center text-emerald-300 font-semibold">{Number(player.career_total_points || 0).toFixed(1)}</td>
                        <td className="py-2 px-3 text-center text-yellow-300 font-semibold">{Number(player.career_wins || 0)}</td>
                        <td className="py-2 px-3 text-center">{Number(player.career_second_places || 0)}</td>
                        <td className="py-2 px-3 text-center">{Number(player.career_third_places || 0)}</td>
                        <td className="py-2 px-3 text-center">{Number(player.career_fourth_places || 0)}</td>
                        <td className="py-2 px-3 text-center text-sky-300">{Number(player.career_draws || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-400">{tr('Aucun joueur avec des points pour cette periode.', 'No players with points for this period.')}</p>
            )}
          </div>
        )}

        {!roundTabParams && selectedStoreId && (
          <>
            {/* Section Joueurs */}
            <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">{language === 'en' ? `Players (${players.length})` : `Joueurs (${players.length})`}</h2>
            <div className="flex gap-2">
              <button
                onClick={() => setPlayersCollapsed(!playersCollapsed)}
                className="px-3 py-1 bg-gray-600 hover:bg-gray-500 rounded text-sm transition-colors"
              >
                {playersCollapsed ? '▼' : '▲'}
              </button>
              <button
                onClick={() => setShowPlayerForm(true)}
                disabled={!canEditCurrentStore}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {language === 'en' ? '+ Add player' : '+ Ajouter un joueur'}
              </button>
              <button
                onClick={() => setShowImportPlayersModal(true)}
                disabled={!canEditCurrentStore}
                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {language === 'en' ? '📥 Import CSV' : '📥 Importer CSV'}
              </button>
            </div>
          </div>

          {!playersCollapsed && (
            <div className="bg-gray-700 rounded-lg p-6">
              {players.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center">
                    <input
                      type="text"
                      placeholder={language === 'en' ? 'Search by name or email...' : 'Chercher par nom ou email...'}
                      value={playerSearchFilter}
                      onChange={(e) => setPlayerSearchFilter(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 text-white placeholder-gray-400"
                    />
                    <label className="inline-flex items-center gap-2 text-sm text-gray-200 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={showRegularPlayersOnly}
                        onChange={(e) => setShowRegularPlayersOnly(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-500 bg-gray-800 text-cyan-500 focus:ring-cyan-400"
                      />
                      {tr('Habitues seulement', 'Regulars only')}
                    </label>
                  </div>
                </div>
              )}
              {sortedPlayers.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full table-auto">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4 font-semibold">{language === 'en' ? 'Name' : 'Nom'}</th>
                        <th className="text-left py-3 px-4 font-semibold">Email</th>
                        <th className="text-center py-3 px-2 font-semibold">{tr('Victoires', 'Wins')}</th>
                        <th className="text-center py-3 px-2 font-semibold">2e</th>
                        <th className="text-center py-3 px-2 font-semibold">3e</th>
                        <th className="text-center py-3 px-2 font-semibold">4e</th>
                        <th className="text-center py-3 px-2 font-semibold">Draw</th>
                        <th className="text-left py-3 px-4 font-semibold">{language === 'en' ? 'Registration date' : 'Date d\'inscription'}</th>
                        <th className="text-left py-3 px-4 font-semibold">{language === 'en' ? 'Actions' : 'Actions'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPlayers.map(player => (
                        <tr key={player.id} className="border-b border-gray-700 hover:bg-gray-700">
                          <td className="py-3 px-4">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <span>{player.name}</span>
                                {player.is_regular && (
                                  <span className="inline-block text-[10px] bg-cyan-800 text-cyan-100 px-2 py-0.5 rounded-full uppercase tracking-wide">
                                    {tr('Habitue', 'Regular')}
                                  </span>
                                )}
                              </div>
                              {getPlayerBadges(player).length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {getPlayerBadges(player).map((badge) => (
                                    <span
                                      key={badge.key}
                                      className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide ${badge.className}`}
                                    >
                                      {badge.label}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">{player.email}</td>
                          <td className="py-3 px-2 text-center text-yellow-300 font-semibold">{Number(player.career_wins || 0)}</td>
                          <td className="py-3 px-2 text-center">{Number(player.career_second_places || 0)}</td>
                          <td className="py-3 px-2 text-center">{Number(player.career_third_places || 0)}</td>
                          <td className="py-3 px-2 text-center">{Number(player.career_fourth_places || 0)}</td>
                          <td className="py-3 px-2 text-center text-sky-300">{Number(player.career_draws || 0)}</td>
                          <td className="py-3 px-4 text-gray-300">
                            {new Date(player.created_at).toLocaleDateString(locale)}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => toggleRegularPlayer(player)}
                                className={`${player.is_regular ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-gray-600 hover:bg-gray-500'} px-3 py-1 rounded text-sm transition-colors`}
                              >
                                {player.is_regular ? tr('Habitue', 'Regular') : tr('Normal', 'Standard')}
                              </button>
                              <button
                                type="button"
                                onClick={() => openEditPlayerForm(player)}
                                className="bg-yellow-500 hover:bg-yellow-600 px-3 py-1 rounded text-sm transition-colors"
                              >
                                {t('edit')}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeletePlayer(player.id)}
                                disabled={loading}
                                className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm transition-colors disabled:opacity-50"
                              >
                                {t('delete')}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : players.length > 0 ? (
                <p className="text-gray-400 text-center py-8">{language === 'en' ? 'No player matches your search' : 'Aucun joueur ne correspond a votre recherche'}</p>
              ) : (
                <p className="text-gray-400 text-center py-8">{language === 'en' ? 'No players saved yet' : 'Aucun joueur enregistre'}</p>
              )}
            </div>
          )}
          </div>

          {/* Section Tournois */}
          <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">{language === 'en' ? `Tournaments (${tournaments.length})` : `Tournois (${tournaments.length})`}</h2>
            <div className="flex gap-2">
              <button
                onClick={() => setTournamentsCollapsed(!tournamentsCollapsed)}
                className="px-3 py-1 bg-gray-600 hover:bg-gray-500 rounded text-sm transition-colors"
              >
                {tournamentsCollapsed ? '▼' : '▲'}
              </button>
              <button
                onClick={() => setShowTournamentForm(true)}
                disabled={!canEditCurrentStore}
                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {language === 'en' ? '+ Create tournament' : '+ Creer un tournoi'}
              </button>
            </div>
          </div>

          {!tournamentsCollapsed && (
            <div className="bg-gray-700 rounded-lg p-6">
              {tournaments.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {tournaments.map((tournament) => (
                    <div
                      key={tournament.id}
                      className="bg-gray-700 p-4 rounded-lg border border-gray-600 hover:border-green-500 cursor-pointer transition-colors"
                      onClick={() => handleTournamentClick(tournament)}
                    >
                      <div className="flex justify-between items-start gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{tournament.name}</h3>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTournament(tournament.id);
                          }}
                          disabled={loading}
                          className="bg-red-700 hover:bg-red-800 px-2 py-1 rounded text-xs transition-colors disabled:opacity-50"
                        >
                          {t('delete')}
                        </button>
                      </div>
                      <p className="text-gray-300 text-sm mb-1">{language === 'en' ? 'Status:' : 'Statut:'} <span className="capitalize">{tournament.status}</span></p>
                      <p className="text-gray-300 text-sm">
                        {language === 'en' ? 'Created on:' : 'Cree le:'} {new Date(tournament.created_at).toLocaleDateString(locale)}
                      </p>
                      {tournament.start_date && (
                        <p className="text-gray-300 text-sm">
                          {language === 'en' ? 'Date:' : 'Date:'} {new Date(tournament.start_date).toLocaleDateString(locale)}
                        </p>
                      )}
                      <p className="text-gray-400 text-xs mt-2">{language === 'en' ? 'Click to manage players' : 'Cliquez pour gerer les joueurs'}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8">{language === 'en' ? 'No tournament created' : 'Aucun tournoi cree'}</p>
              )}
            </div>
          )}
            </div>
          </>
        )}

        {/* Section Détails Tournoi */}
        {selectedTournament && !roundTabParams && !raceTabParams && selectedStoreId && (
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold">{selectedTournament.name}</h2>
              <div className="flex gap-2">
                <button
                  onClick={handleRefreshTournamentView}
                  disabled={loading}
                  className="bg-cyan-600 hover:bg-cyan-700 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {loading ? (language === 'en' ? 'Refreshing...' : 'Rafraichissement...') : (language === 'en' ? 'Refresh' : 'Rafraichir')}
                </button>
                <button
                  onClick={openEditTournamentForm}
                  disabled={!canEditCurrentStore}
                  className="bg-yellow-600 hover:bg-yellow-700 px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  {language === 'en' ? 'Edit' : 'Modifier'}
                </button>
                <button
                  onClick={() => handleDeleteTournament(selectedTournament.id)}
                  disabled={loading || !canEditCurrentStore}
                  className="bg-red-700 hover:bg-red-800 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {language === 'en' ? 'Delete tournament' : 'Supprimer tournoi'}
                </button>
                <button
                  onClick={() => setSelectedTournament(null)}
                  className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  {language === 'en' ? 'Close' : 'Fermer'}
                </button>
              </div>
            </div>

            {(() => {
              const db = calculateDashboard();
              if (db.totalRounds === 0) return null;
              const kpis = [
                {
                  label: tr('Ronde en cours', 'Current round'),
                  value: db.currentRoundNumber,
                  sub: language === 'en'
                    ? `of ${db.totalRounds} round${db.totalRounds > 1 ? 's' : ''}`
                    : `sur ${db.totalRounds} round${db.totalRounds > 1 ? 's' : ''}`,
                  color: 'text-indigo-400',
                },
                {
                  label: tr('Tables terminees', 'Completed tables'),
                  value: `${db.tablesCompletedCurrentRound} / ${db.tablesInLastRound}`,
                  sub: db.tablesPendingCurrentRound > 0
                    ? (language === 'en' ? `${db.tablesPendingCurrentRound} pending` : `${db.tablesPendingCurrentRound} en attente`)
                    : tr('Ronde complete ✓', 'Round complete ✓'),
                  color: db.tablesPendingCurrentRound === 0 ? 'text-green-400' : 'text-yellow-400',
                },
                {
                  label: tr('Joueurs sans resultat', 'Players without result'),
                  value: db.playersWithoutResultCurrentRound,
                  sub: tr('ronde courante', 'current round'),
                  color: db.playersWithoutResultCurrentRound > 0 ? 'text-red-400' : 'text-green-400',
                },
                {
                  label: tr('Joueurs actifs', 'Active players'),
                  value: db.activePlayers,
                  sub: language === 'en'
                    ? `${db.droppedCount} drop${db.droppedCount !== 1 ? 's' : ''} · ${db.absentCount} absent${db.absentCount !== 1 ? 's' : ''} next`
                    : `${db.droppedCount} drop${db.droppedCount !== 1 ? 's' : ''} · ${db.absentCount} absent${db.absentCount !== 1 ? 's' : ''} R+`,
                  color: 'text-cyan-400',
                },
                {
                  label: tr('Progression globale', 'Overall progress'),
                  value: `${db.progressPercent}%`,
                  sub: tr('tables avec resultats', 'tables with results'),
                  color: 'text-teal-400',
                },
              ];
              return (
                <div className="mt-4 mb-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {kpis.map((kpi) => (
                    <div key={kpi.label} className="bg-gray-700 rounded-xl p-3 flex flex-col gap-1">
                      <p className="text-gray-400 text-xs font-medium uppercase tracking-wide leading-tight">{kpi.label}</p>
                      <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
                      <p className="text-gray-500 text-xs">{kpi.sub}</p>
                    </div>
                  ))}
                </div>
              );
            })()}

            {(() => {
              const tournamentResults = calculateTournamentResults();
              return tournamentResults.length > 0 ? (
                <div className="mt-6 bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4">{tr('Classement du tournoi', 'Tournament standings')}</h3>
                  <p className="text-gray-400 text-sm mb-4">{tr('Classement en direct base sur les resultats saisis.', 'Live ranking based on entered results.')}</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-600">
                          <th className="text-left py-2 px-3">{tr('Position', 'Position')}</th>
                          <th className="text-left py-2 px-3">{tr('Joueur', 'Player')}</th>
                          <th className="text-center py-2 px-3">{tr('Statut', 'Status')}</th>
                          <th className="text-center py-2 px-3">{tr('Points', 'Points')}</th>
                          <th className="text-center py-2 px-3">{tr('Victoires', 'Wins')}</th>
                          <th className="text-center py-2 px-3">{tr('Nuls', 'Draws')}</th>
                          <th className="text-center py-2 px-3">TB OppPts</th>
                          <th className="text-center py-2 px-3">TB OppWins</th>
                          <th className="text-center py-2 px-3">{tr('Rounds joues', 'Rounds played')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tournamentResults.map((result, index) => (
                          <tr key={result.id} className="border-b border-gray-600">
                            <td className="py-2 px-3 font-medium">{index + 1}</td>
                            <td className="py-2 px-3">{result.name}</td>
                            <td className="py-2 px-3 text-center">
                              {droppedPlayerIds.includes(result.id) ? (
                                <span className="inline-block text-xs bg-red-800 text-red-100 px-2 py-0.5 rounded">{tr('Drop', 'Dropped')}</span>
                              ) : (
                                <span className="text-xs text-gray-400">{tr('Actif', 'Active')}</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center font-semibold text-yellow-400">{result.totalPoints}</td>
                            <td className="py-2 px-3 text-center">{result.wins}</td>
                            <td className="py-2 px-3 text-center">{result.draws}</td>
                            <td className="py-2 px-3 text-center">{formatTiebreakValue(result.oppAvgPoints)}</td>
                            <td className="py-2 px-3 text-center">{formatTiebreakValue(result.oppAvgWins)}</td>
                            <td className="py-2 px-3 text-center">{result.roundsPlayed}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="mt-6 bg-gray-700 p-4 rounded-lg">
                  <p className="text-gray-400 text-center">{tr('Aucun resultat disponible pour ce tournoi.', 'No results available for this tournament.')}</p>
                </div>
              );
            })()}

            {renderDragonRaceSection(false)}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-3">{tr('Joueurs participants', 'Participating players')} ({tournamentPlayers.length})</h3>
                {tournamentPlayers.length > 0 ? (
                  <div className="space-y-2">
                    {sortedTournamentPlayers.map((player) => (
                      <div key={player.id} className="flex justify-between items-center bg-gray-700 p-3 rounded-lg">
                        <div>
                          <p className="font-medium">
                            {player.name}
                            {droppedPlayerIds.includes(player.id) && (
                              <span className="ml-2 text-xs bg-red-800 text-red-100 px-2 py-0.5 rounded">{tr('Drop', 'Dropped')}</span>
                            )}
                            {!droppedPlayerIds.includes(player.id) && absentNextRoundIds.includes(player.id) && (
                              <span className="ml-2 text-xs bg-yellow-700 text-yellow-100 px-2 py-0.5 rounded">{tr('Absent prochain round', 'Absent next round')}</span>
                            )}
                          </p>
                          <p className="text-gray-300 text-sm">{player.email}</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => toggleAbsentNextRound(player.id)}
                            disabled={loading || droppedPlayerIds.includes(player.id)}
                            className="bg-yellow-600 hover:bg-yellow-700 px-2 py-1 rounded text-xs transition-colors disabled:opacity-50"
                          >
                            {absentNextRoundIds.includes(player.id) ? tr('Present', 'Present') : tr('Absent R+', 'Absent next')}
                          </button>
                          <button
                            onClick={() => toggleDroppedPlayer(player.id)}
                            disabled={loading}
                            className="bg-orange-600 hover:bg-orange-700 px-2 py-1 rounded text-xs transition-colors disabled:opacity-50"
                          >
                            {droppedPlayerIds.includes(player.id) ? tr('Reactiver', 'Reactivate') : 'Drop'}
                          </button>
                          <button
                            onClick={() => handleRemovePlayerFromTournament(player.id)}
                            disabled={loading || !canEditCurrentStore}
                            className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm transition-colors disabled:opacity-50"
                          >
                            {tr('Retirer', 'Remove')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400">{tr('Aucun joueur participant', 'No participating players')}</p>
                )}
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3">{tr('Ajouter des joueurs', 'Add players')}</h3>
                {getAvailablePlayers().length > 0 ? (
                  <div className="space-y-2">
                    {getAvailablePlayers().map((player) => (
                      <div key={player.id} className="flex justify-between items-center bg-gray-700 p-3 rounded-lg">
                        <div>
                          <p className="font-medium flex items-center gap-2">
                            <span>{player.name}</span>
                            {player.is_regular && <span className="text-[10px] bg-cyan-800 text-cyan-100 px-2 py-0.5 rounded-full uppercase tracking-wide">{tr('Habitue', 'Regular')}</span>}
                          </p>
                          <p className="text-gray-300 text-sm">{player.email}</p>
                        </div>
                        <button
                          onClick={() => handleAddPlayerToTournament(player.id)}
                          disabled={loading || !canEditCurrentStore}
                          className="bg-green-600 hover:bg-green-700 px-3 py-1 rounded text-sm transition-colors disabled:opacity-50"
                        >
                          {tr('Ajouter', 'Add')}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400">{tr('Tous les joueurs sont deja participants', 'All players are already participating')}</p>
                )}
              </div>
            </div>

            <div className="mt-6 bg-gray-700 p-4 rounded-lg">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
                <div>
                  <h3 className="text-lg font-semibold">{tr('Rounds et tables', 'Rounds and tables')}</h3>
                  <p className="text-gray-400 text-sm">{tr('Creer des tables sans repetition de joueurs.', 'Create tables without repeating players.')}</p>
                  <p className="text-gray-500 text-xs mt-1">
                    {tr('Exclus du prochain round', 'Excluded from next round')}: {new Set([...absentNextRoundIds, ...droppedPlayerIds]).size} {tr('joueur(s)', 'player(s)')}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <label htmlFor="round-duration-minutes" className="text-sm text-gray-300">{tr('Duree ronde (min)', 'Round duration (min)')}</label>
                  <input
                    id="round-duration-minutes"
                    type="number"
                    min="1"
                    max="300"
                    step="1"
                    value={roundDurationMinutes}
                    onChange={(e) => setRoundDurationMinutes(clampRoundTimerMinutes(e.target.value))}
                    className="w-24 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={handleGenerateRound}
                    disabled={loading || !canEditCurrentStore || tournamentPlayers.filter((p) => !droppedPlayerIds.includes(p.id) && !absentNextRoundIds.includes(p.id)).length < 3}
                    className="bg-indigo-600 hover:bg-indigo-700 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? tr('Generation...', 'Generating...') : tr('Generer un round', 'Generate round')}
                  </button>
                  {tournamentRounds.length > 0 && (
                    <button
                      onClick={() => openRoundFullscreenView(tournamentRounds[tournamentRounds.length - 1])}
                      className="bg-teal-600 hover:bg-teal-700 px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      {tr('Ronde en cours (plein ecran)', 'Current round (fullscreen)')}
                    </button>
                  )}
                </div>
              </div>

              {tournamentRounds.length > 0 ? (
                <div className="space-y-4">
                  {tournamentRounds.map((round) => (
                    <div key={round.id} className="bg-gray-800 p-4 rounded-lg border border-gray-600">
                      <div className="flex items-center justify-between mb-3 gap-4">
                        <div>
                          <p className="font-semibold">Round {round.round_number}</p>
                          <p className="text-gray-400 text-sm">{tr('Tables', 'Tables')}: {round.tables.length}</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => openRoundView(round)}
                            className="bg-cyan-600 hover:bg-cyan-700 px-3 py-1 rounded text-sm transition-colors"
                          >
                            {tr('Voir la ronde', 'View round')}
                          </button>
                          <button
                            onClick={() => openRoundFullscreenView(round)}
                            className="bg-teal-600 hover:bg-teal-700 px-3 py-1 rounded text-sm transition-colors"
                          >
                            {tr('Plein ecran', 'Fullscreen')}
                          </button>
                          <button
                            onClick={() => handleDeleteRound(round.id)}
                            disabled={loading}
                            className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm transition-colors disabled:opacity-50"
                          >
                            {t('delete')}
                          </button>
                        </div>
                      </div>

                      <div className="mb-3">
                        {renderRoundTimerPanel(round, true)}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {round.tables.map((table) => {
                          const tableResults = roundResults.find(
                            (tr) => tr.roundId === round.id && tr.tableNumber === table.tableNumber
                          );
                          return (
                            <div key={table.tableNumber} className="bg-gray-700 p-3 rounded-lg border border-gray-600">
                              <div className="flex justify-between items-center mb-2">
                                <p className="font-medium">Table {table.tableNumber}</p>
                                <button
                                  onClick={() => startEditingTable(round.id, table)}
                                  disabled={!canEditCurrentStore}
                                  className="bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                                >
                                  {tableResults ? tr('Modifier resultats', 'Edit results') : tr('Saisir resultats', 'Enter results')}
                                </button>
                              </div>
                              <ul className="space-y-1 text-gray-300 text-sm mb-2">
                                {sortPlayersByName(table.players).map((player) => {
                                  const result = tableResults?.results.find((r) => r.player.id === player.id);
                                  return (
                                    <li key={player.id} className="flex justify-between">
                                      <span>{player.name}</span>
                                      {result && (
                                        <span className="text-yellow-400">
                                          {result.position}{result.isDraw ? ' (draw)' : (language === 'en' ? '' : 'e')} - {result.pointsAwarded} {tr('pts', 'pts')}
                                        </span>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400">{tr('Aucun round cree pour ce tournoi.', 'No round created for this tournament.')}</p>
              )}
            </div>
          </div>
        )}

        {selectedTournament && raceTabParams && selectedStoreId && renderDragonRaceSection(true, true)}

        {showStoreForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-xl font-semibold mb-4">{tr('Creer un magasin', 'Create store')}</h3>
              <form onSubmit={handleCreateStore}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">{tr('Nom du magasin', 'Store name')}</label>
                    <input
                      type="text"
                      value={newStoreName}
                      onChange={(e) => setNewStoreName(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-emerald-500"
                      placeholder="Ex: Face to Face Montreal"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">{tr('Image d\'entete', 'Header image')}</label>
                    <label
                      className={`relative flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl px-4 py-6 text-center transition-colors cursor-pointer ${isCreateStoreImageDragging ? 'border-emerald-400 bg-emerald-500/10' : 'border-gray-600 bg-gray-700/50 hover:border-emerald-500 hover:bg-gray-700'}`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsCreateStoreImageDragging(true);
                      }}
                      onDragLeave={() => setIsCreateStoreImageDragging(false)}
                      onDrop={(e) => handleStoreImageDrop(e, setNewStoreImageUrl, setIsCreateStoreImageDragging)}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => importStoreHeaderImage(e.target.files?.[0], setNewStoreImageUrl)}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                      />
                      <span className="text-sm font-medium text-white">{tr('Glisse une image ici ou clique pour choisir un fichier', 'Drop an image here or click to choose a file')}</span>
                      <span className="text-xs text-gray-400">{tr('Optimisation automatique avant envoi, 1800x720 max.', 'Automatic optimization before upload, 1800x720 max.')}</span>
                    </label>
                    <p className="text-xs text-gray-400 mt-2">{tr('L\'app redimensionne et compresse automatiquement l\'image importee.', 'The app automatically resizes and compresses the uploaded image.')}</p>
                    {newStoreImageUrl && (
                      <div className="mt-3 space-y-3 rounded-xl border border-gray-700 bg-gray-900/50 p-3">
                        <img
                          src={newStoreImageUrl}
                          alt={tr('Apercu entete magasin', 'Store header preview')}
                          className="w-full h-36 object-cover rounded-lg border border-gray-600"
                        />
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-xs text-gray-400">{tr('Apercu de l\'image optimisee qui sera enregistree.', 'Preview of the optimized image that will be saved.')}</p>
                          <button
                            type="button"
                            onClick={() => setNewStoreImageUrl('')}
                            className="text-sm text-red-300 hover:text-red-200 transition-colors"
                          >
                            {tr('Retirer l\'image', 'Remove image')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowStoreForm(false);
                      setNewStoreName('');
                      setNewStoreImageUrl('');
                      setIsCreateStoreImageDragging(false);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg transition-colors"
                  >
                    {tr('Annuler', 'Cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {loading ? tr('Creation...', 'Creating...') : tr('Creer', 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showEditStoreForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-xl font-semibold mb-4">{tr('Modifier le magasin', 'Edit store')}</h3>
              <form onSubmit={handleUpdateStore}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">{tr('Nom du magasin', 'Store name')}</label>
                    <input
                      type="text"
                      value={editStoreName}
                      onChange={(e) => setEditStoreName(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-yellow-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">{tr('Image d\'entete', 'Header image')}</label>
                    <label
                      className={`relative flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl px-4 py-6 text-center transition-colors cursor-pointer ${isEditStoreImageDragging ? 'border-yellow-400 bg-yellow-500/10' : 'border-gray-600 bg-gray-700/50 hover:border-yellow-500 hover:bg-gray-700'}`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsEditStoreImageDragging(true);
                      }}
                      onDragLeave={() => setIsEditStoreImageDragging(false)}
                      onDrop={(e) => handleStoreImageDrop(e, setEditStoreImageUrl, setIsEditStoreImageDragging)}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => importStoreHeaderImage(e.target.files?.[0], setEditStoreImageUrl)}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                      />
                      <span className="text-sm font-medium text-white">{tr('Depose une nouvelle image ou clique pour en choisir une', 'Drop a new image or click to choose one')}</span>
                      <span className="text-xs text-gray-400">{tr('L\'image est redimensionnee automatiquement avant enregistrement.', 'The image is automatically resized before saving.')}</span>
                    </label>
                    <p className="text-xs text-gray-400 mt-2">{tr('Tu peux conserver l\'image actuelle ou la remplacer par un nouveau fichier.', 'You can keep the current image or replace it with a new file.')}</p>
                    {editStoreImageUrl && (
                      <div className="mt-3 space-y-3 rounded-xl border border-gray-700 bg-gray-900/50 p-3">
                        <img
                          src={editStoreImageUrl}
                          alt={tr('Apercu entete magasin', 'Store header preview')}
                          className="w-full h-36 object-cover rounded-lg border border-gray-600"
                        />
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-xs text-gray-400">{tr('Apercu de l\'image qui sera enregistree pour ce magasin.', 'Preview of the image that will be saved for this store.')}</p>
                          <button
                            type="button"
                            onClick={() => setEditStoreImageUrl('')}
                            className="text-sm text-red-300 hover:text-red-200 transition-colors"
                          >
                            {tr('Supprimer l\'image', 'Delete image')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditStoreForm(false);
                      setEditStoreName('');
                      setEditStoreImageUrl('');
                      setIsEditStoreImageDragging(false);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg transition-colors"
                  >
                    {tr('Annuler', 'Cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {loading ? tr('Enregistrement...', 'Saving...') : tr('Enregistrer', 'Save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Formulaire Ajout Joueur */}
        {showPlayerForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
            <div className="bg-gray-800 rounded-lg p-4 sm:p-6 w-full sm:max-w-md max-h-[95vh] overflow-y-auto">
              <h3 className="text-lg sm:text-xl font-semibold mb-4">
                {editingPlayer
                  ? (language === 'en' ? 'Edit player' : 'Modifier le joueur')
                  : (language === 'en' ? 'Add player' : 'Ajouter un joueur')}
              </h3>
              <form onSubmit={handleSavePlayer}>
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{tr('Nom', 'Name')}</label>
                    <input
                      type="text"
                      value={newPlayer.name}
                      onChange={(e) => setNewPlayer({...newPlayer, name: e.target.value})}
                      className="w-full px-3 py-2 text-base bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                      placeholder={language === 'en' ? 'Enter player name' : 'Entrez le nom du joueur'}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <input
                      type="email"
                      value={newPlayer.email}
                      onChange={(e) => setNewPlayer({...newPlayer, email: e.target.value})}
                      className="w-full px-3 py-2 text-base bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                      placeholder={language === 'en' ? 'Enter player email' : 'Entrez l\'email du joueur'}
                      required
                    />
                  </div>
                  <label className="flex items-center gap-2 bg-gray-700 rounded-lg px-3 py-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(newPlayer.is_regular)}
                      onChange={(e) => setNewPlayer({ ...newPlayer, is_regular: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{tr('Joueur habitue', 'Regular player')}</span>
                  </label>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 sm:mt-6">
                  <button
                    type="button"
                    onClick={closePlayerForm}
                    disabled={loading}
                    className="flex-1 px-4 py-3 sm:py-2 text-base sm:text-sm bg-gray-600 hover:bg-gray-500 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {language === 'en' ? 'Cancel' : 'Annuler'}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-3 sm:py-2 text-base sm:text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {loading && <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                    {loading
                      ? (editingPlayer
                        ? (language === 'en' ? 'Saving...' : 'Enregistrement...')
                        : (language === 'en' ? 'Adding...' : 'Ajout...'))
                      : (editingPlayer
                        ? (language === 'en' ? 'Save' : 'Enregistrer')
                        : (language === 'en' ? 'Add' : 'Ajouter'))}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Import Joueurs CSV */}
        {showImportPlayersModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
            <div className="bg-gray-800 rounded-lg p-4 sm:p-6 w-full sm:max-w-md max-h-[95vh] overflow-y-auto">
              <h3 className="text-lg sm:text-xl font-semibold mb-4">{language === 'en' ? 'Import players (CSV)' : 'Importer des joueurs (CSV)'}</h3>
              
              <div className="mb-4 p-3 sm:p-4 bg-gray-700 rounded-lg">
                <p className="text-xs text-gray-300 mb-2">{language === 'en' ? 'Expected format (CSV, comma-separated):' : 'Format attendu (CSV, separe par virgule):'}</p>
                <code className="text-xs bg-gray-900 p-2 block rounded text-cyan-300 overflow-x-auto">Nom,Email
Jean,jean@ex.com</code>
              </div>

              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-medium mb-2">{language === 'en' ? 'CSV file' : 'Fichier CSV'}</label>
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      handleImportPlayersCSV(e.target.files[0]);
                      setShowImportPlayersModal(false);
                    }
                  }}
                  disabled={importingPlayers}
                  className="w-full px-4 py-2 text-base bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-green-500 disabled:opacity-50"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => setShowImportPlayersModal(false)}
                  disabled={importingPlayers}
                  className="flex-1 px-4 py-3 sm:py-2 text-base sm:text-sm bg-gray-600 hover:bg-gray-500 rounded-lg transition-colors disabled:opacity-50"
                >
                  {language === 'en' ? 'Cancel' : 'Annuler'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Formulaire Création Tournoi */}
        {showTournamentForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
            <div className="bg-gray-800 rounded-lg p-4 sm:p-6 w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg sm:text-xl font-semibold mb-4">{language === 'en' ? 'Create tournament' : 'Creer un tournoi'}</h3>
              <form onSubmit={handleCreateTournament}>
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{language === 'en' ? 'Tournament name' : 'Nom du tournoi'}</label>
                    <input
                      type="text"
                      value={newTournament.name}
                      onChange={(e) => setNewTournament({...newTournament, name: e.target.value})}
                      className="w-full px-3 py-2 text-base bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-green-500"
                      placeholder={language === 'en' ? 'Enter tournament name' : 'Entrez le nom du tournoi'}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">{language === 'en' ? 'Select participating players' : 'Selectionner les joueurs participants'}</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                      {sortedPlayers.map(player => (
                        <label key={player.id} className="flex items-center space-x-2 p-2 bg-gray-700 rounded-lg hover:bg-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newTournament.selectedPlayers.includes(player.id)}
                            onChange={() => handlePlayerSelection(player.id)}
                            className="w-4 h-4 text-green-600 bg-gray-700 border-gray-600 rounded focus:ring-green-500"
                          />
                          <span>
                            {player.name} {player.is_regular ? `(${tr('Habitue', 'Regular')})` : ''} ({player.email})
                          </span>
                        </label>
                      ))}
                    </div>
                    {players.length === 0 && (
                      <p className="text-gray-400 text-center py-4">{language === 'en' ? 'No players available. Add players first.' : 'Aucun joueur disponible. Ajoutez d\'abord des joueurs.'}</p>
                    )}
                  </div>

                  {newTournament.selectedPlayers.length > 0 && (
                    <div className="bg-gray-700 p-3 rounded-lg">
                      <p className="text-sm text-gray-300">
                        {language === 'en'
                          ? `${newTournament.selectedPlayers.length} player${newTournament.selectedPlayers.length > 1 ? 's' : ''} selected`
                          : `${newTournament.selectedPlayers.length} joueur${newTournament.selectedPlayers.length > 1 ? 's' : ''} selectionne${newTournament.selectedPlayers.length > 1 ? 's' : ''}`}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 sm:mt-6">
                  <button
                    type="button"
                    onClick={() => setShowTournamentForm(false)}
                    disabled={loading}
                    className="flex-1 px-4 py-3 sm:py-2 text-base sm:text-sm bg-gray-600 hover:bg-gray-500 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {language === 'en' ? 'Cancel' : 'Annuler'}
                  </button>
                  <button
                    type="submit"
                    disabled={loading || newTournament.selectedPlayers.length === 0}
                    className="flex-1 px-4 py-3 sm:py-2 text-base sm:text-sm bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {loading && <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                    {loading ? (language === 'en' ? 'Creating...' : 'Creation...') : (language === 'en' ? 'Create tournament' : 'Creer le tournoi')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {editingTable && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex sm:items-center sm:justify-center sm:p-4 z-50">
            <div className="bg-gray-800 sm:rounded-xl p-4 sm:p-6 w-full sm:max-w-2xl h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold">Table {editingTable.tableNumber}</h3>
                <button
                  type="button"
                  onClick={cancelEditingTable}
                  className="text-gray-400 hover:text-white text-3xl leading-none w-10 h-10 flex items-center justify-center"
                >
                  &times;
                </button>
              </div>
              <form
                className="flex-1 flex flex-col"
                onSubmit={(e) => {
                  e.preventDefault();
                  const sortedEditingPlayers = sortPlayersByName(editingTable.players);
                  const results = sortedEditingPlayers.map((player) => ({
                    playerId: player.id,
                    position: parseInt(editingTableDraft[player.id]?.position, 10),
                    isDraw: Boolean(editingTableDraft[player.id]?.isDraw),
                  }));
                  handleSaveTableResults(editingRoundId, editingTable.tableNumber, results);
                }}
              >
                <p className="text-cyan-300 text-xs bg-cyan-900/30 p-2 rounded border border-cyan-800/50 mb-4">
                  {tr('Brouillon auto active - la saisie est enregistree automatiquement.', 'Auto draft enabled - input is saved automatically.')}
                </p>
                <div className="space-y-4 flex-1">
                  {sortPlayersByName(editingTable.players).map((player) => (
                    <div key={player.id} className="p-4 bg-gray-700 rounded-xl space-y-3">
                      <p className="font-bold text-xl leading-tight">{player.name}</p>
                      <div>
                        <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">{tr('Position', 'Position')}</p>
                        <div className="flex gap-3 flex-wrap">
                          {sortPlayersByName(editingTable.players).map((_, posIndex) => {
                            const pos = String(posIndex + 1);
                            const isSelected = editingTableDraft[player.id]?.position === pos;
                            const takenByOther = !isSelected && Object.entries(editingTableDraft).some(
                              ([pid, draft]) => pid !== String(player.id) && draft.position === pos
                            );
                            return (
                              <button
                                key={pos}
                                type="button"
                                disabled={takenByOther}
                                onClick={() =>
                                  setEditingTableDraft((prev) => ({
                                    ...prev,
                                    [player.id]: {
                                      position: prev[player.id]?.position === pos ? '' : pos,
                                      isDraw: Boolean(prev[player.id]?.isDraw),
                                    },
                                  }))
                                }
                                className={`w-16 h-16 rounded-2xl text-2xl font-bold transition-colors ${
                                  isSelected
                                    ? 'bg-indigo-600 ring-2 ring-indigo-400 text-white'
                                    : takenByOther
                                    ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                                    : 'bg-gray-600 hover:bg-gray-500 text-gray-200'
                                }`}
                              >
                                {pos}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setEditingTableDraft((prev) => ({
                            ...prev,
                            [player.id]: {
                              position: prev[player.id]?.position || '',
                              isDraw: !prev[player.id]?.isDraw,
                            },
                          }))
                        }
                        className={`w-full py-4 rounded-xl font-semibold transition-colors text-base ${
                          editingTableDraft[player.id]?.isDraw
                            ? 'bg-blue-600 text-white ring-2 ring-blue-400'
                            : 'bg-gray-600 hover:bg-gray-500 text-gray-300'
                        }`}
                      >
                        {editingTableDraft[player.id]?.isDraw ? tr('✓ Draw active', '✓ Draw enabled') : 'Draw'}
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 mt-6 pt-4 border-t border-gray-700">
                  <button
                    type="button"
                    onClick={cancelEditingTable}
                    className="flex-1 py-4 text-base bg-gray-600 hover:bg-gray-500 rounded-xl transition-colors"
                  >
                    {tr('Annuler', 'Cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 py-4 text-base bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-bold transition-colors"
                  >
                    {loading ? tr('Sauvegarde...', 'Saving...') : tr('Sauvegarder', 'Save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {selectedRoundView && roundTabParams && (
          <div className={roundTabParams.fullscreen ? 'min-h-screen px-4 py-6 md:px-6 md:py-8' : 'bg-gray-800 rounded-lg p-6'}>
            <div className="flex items-center justify-between mb-4 gap-3">
              <div>
                <h3 className="text-2xl font-semibold">Round {selectedRoundView.round_number}</h3>
                <p className="text-gray-400 text-sm">
                  {roundTabParams.fullscreen
                    ? tr('Ronde en cours - affichage plein ecran', 'Current round - fullscreen display')
                    : tr('Toutes les tables de la ronde', 'All tables in this round')}
                </p>
              </div>
              {!roundTabParams.fullscreen && (
                <button
                  onClick={() => {
                    window.location.href = '/';
                  }}
                  className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  {tr('Retour accueil', 'Back home')}
                </button>
              )}
            </div>

            <div className="mb-4">
              {renderRoundTimerPanel(selectedRoundView, false, roundTabParams.fullscreen)}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedRoundView.tables.map((table) => {
                const tableResults = roundResults.find(
                  (tr) => tr.roundId === selectedRoundView.id && tr.tableNumber === table.tableNumber
                );

                return (
                  <div key={table.tableNumber} className="bg-gray-700 p-5 rounded-xl border border-gray-600">
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-bold text-lg">Table {table.tableNumber}</p>
                      <button
                        onClick={() => startEditingTable(selectedRoundView.id, table)}
                        disabled={!canEditCurrentStore}
                        className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                      >
                        {tableResults ? tr('Modifier resultats', 'Edit results') : tr('Saisir resultats', 'Enter results')}
                      </button>
                    </div>
                    <ul className="space-y-2 text-base text-gray-300">
                      {sortPlayersByName(table.players).map((player) => {
                        const result = tableResults?.results.find((r) => r.player.id === player.id);
                        return (
                          <li key={player.id} className="flex justify-between">
                            <span>{player.name}</span>
                            {result && (
                              <span className="text-yellow-400">
                                {result.position}{result.isDraw ? ' (draw)' : (language === 'en' ? '' : 'e')} - {result.pointsAwarded} {tr('pts', 'pts')}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>

            {(() => {
              const tournamentResults = calculateTournamentResults();
              return (
                <div className="mt-6 bg-gray-700 p-4 rounded-lg">
                  <h4 className="text-lg font-semibold mb-4">{tr('Tableau de resultats', 'Results board')}</h4>
                  {tournamentResults.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-600">
                            <th className="text-left py-2 px-3">{tr('Position', 'Position')}</th>
                            <th className="text-left py-2 px-3">{tr('Joueur', 'Player')}</th>
                            <th className="text-center py-2 px-3">{tr('Statut', 'Status')}</th>
                            <th className="text-center py-2 px-3">{tr('Points', 'Points')}</th>
                            <th className="text-center py-2 px-3">{tr('Victoires', 'Wins')}</th>
                            <th className="text-center py-2 px-3">{tr('Nuls', 'Draws')}</th>
                            <th className="text-center py-2 px-3">TB OppPts</th>
                            <th className="text-center py-2 px-3">TB OppWins</th>
                            <th className="text-center py-2 px-3">{tr('Rounds joues', 'Rounds played')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tournamentResults.map((result, index) => (
                            <tr key={result.id} className="border-b border-gray-600">
                              <td className="py-2 px-3 font-medium">{index + 1}</td>
                              <td className="py-2 px-3">{result.name}</td>
                              <td className="py-2 px-3 text-center">
                                {droppedPlayerIds.includes(result.id) ? (
                                  <span className="inline-block text-xs bg-red-800 text-red-100 px-2 py-0.5 rounded">{tr('Drop', 'Dropped')}</span>
                                ) : (
                                  <span className="text-xs text-gray-400">{tr('Actif', 'Active')}</span>
                                )}
                              </td>
                              <td className="py-2 px-3 text-center font-semibold text-yellow-400">{result.totalPoints}</td>
                              <td className="py-2 px-3 text-center">{result.wins}</td>
                              <td className="py-2 px-3 text-center">{result.draws}</td>
                              <td className="py-2 px-3 text-center">{formatTiebreakValue(result.oppAvgPoints)}</td>
                              <td className="py-2 px-3 text-center">{formatTiebreakValue(result.oppAvgWins)}</td>
                              <td className="py-2 px-3 text-center">{result.roundsPlayed}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-400">{tr('Aucun resultat disponible pour ce tournoi.', 'No results available for this tournament.')}</p>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Modal Formulaire Édition Tournoi */}
        {showEditTournamentForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-lg p-6 w-full max-w-xl">
              <h3 className="text-xl font-semibold mb-4">{tr('Modifier le tournoi', 'Edit tournament')}</h3>
              <form onSubmit={handleUpdateTournament}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{tr('Nom du tournoi', 'Tournament name')}</label>
                    <input
                      type="text"
                      value={editTournament.name}
                      onChange={(e) => setEditTournament({ ...editTournament, name: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-yellow-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{tr('Statut', 'Status')}</label>
                    <select
                      value={editTournament.status}
                      onChange={(e) => setEditTournament({ ...editTournament, status: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-yellow-500"
                    >
                      <option value="pending">{tr('En attente', 'Pending')}</option>
                      <option value="ongoing">{tr('En cours', 'Ongoing')}</option>
                      <option value="finished">{tr('Termine', 'Finished')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">{tr('Joueurs participants', 'Participating players')}</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                      {sortedPlayers.map(player => (
                        <label key={player.id} className="flex items-center space-x-2 p-2 bg-gray-700 rounded-lg hover:bg-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editTournament.selectedPlayers.includes(player.id)}
                            onChange={() => handleEditTournamentPlayerSelection(player.id)}
                            className="w-4 h-4 text-yellow-600 bg-gray-700 border-gray-600 rounded focus:ring-yellow-500"
                          />
                          <span>
                            {player.name} {player.is_regular ? `(${tr('Habitue', 'Regular')})` : ''} ({player.email})
                          </span>
                        </label>
                      ))}
                    </div>
                    {players.length === 0 && (
                      <p className="text-gray-400 text-center py-4">{tr('Aucun joueur disponible.', 'No players available.')}</p>
                    )}
                  </div>

                  <div className="bg-gray-700 p-3 rounded-lg">
                    <p className="text-sm text-gray-300">
                      {language === 'en'
                        ? `${editTournament.selectedPlayers.length} player${editTournament.selectedPlayers.length > 1 ? 's' : ''} selected`
                        : `${editTournament.selectedPlayers.length} joueur${editTournament.selectedPlayers.length > 1 ? 's' : ''} selectionne${editTournament.selectedPlayers.length > 1 ? 's' : ''}`}
                    </p>
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowEditTournamentForm(false)}
                    className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg transition-colors"
                  >
                    {tr('Annuler', 'Cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                  >
                    {loading ? tr('Enregistrement...', 'Saving...') : tr('Enregistrer', 'Save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showHelpModal && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-semibold">{userDocumentation.title}</h2>
                  <p className="text-gray-400 text-sm">Derniere mise a jour: {userDocumentation.lastUpdated}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowHelpModal(false)}
                  className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded-lg transition-colors"
                >
                  Fermer
                </button>
              </div>

              <div className="space-y-5">
                {userDocumentation.sections.map((section) => (
                  <div key={section.title} className="bg-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-semibold mb-2">{section.title}</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-200">
                      {section.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-[100] space-y-2 max-w-sm">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`p-4 rounded-lg shadow-lg animate-in fade-in slide-in-from-top-2 duration-300 text-sm font-medium flex items-center gap-3 ${
              notification.type === 'success' ? 'bg-green-600 text-white' :
              notification.type === 'error' ? 'bg-red-600 text-white' :
              'bg-blue-600 text-white'
            }`}
          >
            <span>
              {notification.type === 'success' ? '✓' :
               notification.type === 'error' ? '✕' :
               'ℹ'}
            </span>
            <span>{notification.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default App;
